/**
 * The UMED operations workspace's surface (EP-004U phases 2–3).
 *
 * Every type mirrors what `readModel.js` returns. Phase 3 adds the native
 * writes — each behind its own capability and, on the server, behind a
 * structural gate: `scope.writes` says NATIVE only on a process where
 * UMED_NATIVE_WRITES is enabled, and `scope.can` says which of the acts this
 * caller holds. A control is offered only when both are true; the server
 * decides regardless.
 *
 * The labels below are how the screen says the business's words to a person.
 * Vocabulary v2 is provisional; the words are the business's, not ours.
 */
import { api, apiUpload, apiDownloadFile } from '../../services/api';

export type Stage = 'CREATED' | 'NEEDS_DCF_ACCOUNT' | 'READY' | 'SUBMITTED' | 'APPROVED' | 'HANDED_TO_FINANCE' | 'CLOSED' | 'UNKNOWN';
export type UmedType = 'NEW' | 'REPLACEMENT' | 'CORRECTION' | 'CROSSOVER' | 'REVERSAL' | 'UNKNOWN';
export type ExceptionKind = 'NONE' | 'CORRECTION' | 'CROSSOVER' | 'REVERSAL' | 'PROBLEM' | 'RESUBMISSION';
export type ExceptionState = 'OPEN' | 'RESUBMITTED' | 'NOT_APPROVED' | 'COMPLETE';
export type Lifecycle = 'DECEASED' | 'NO_LONGER_QUALIFIES' | 'NEEDS_RECERT';
export type FinanceState = 'NOT_OPEN' | 'PORTFOLIO' | 'PAID_IN_FULL' | 'PAID_IN_FULL_CORRECTION';
export type Noca = 'UNKNOWN' | 'PENDING' | 'RECEIVED' | 'REJECTED' | 'WITHHELD';
export type Flmmis = 'UNKNOWN' | 'NOT_REFLECTED' | 'REFLECTED' | 'EXCEPTION' | 'WITHHELD';
export type DcfAccount = 'UNKNOWN' | 'REQUIRED' | 'NOT_REQUIRED' | 'PENDING' | 'READY' | 'BLOCKED';
export type Blocker = 'SSN_REQUIREMENT' | 'SIGNED_UMED' | 'NEW_UMED' | 'AUDIOGRAM' | 'STUCK';
export type ActionType =
  | 'CREATE_DCF_ACCOUNT' | 'OBTAIN_READINESS_EVIDENCE' | 'GENERATE_UMED' | 'VERIFY_UMED' | 'ASSEMBLE_PACKET'
  | 'SUBMIT_TO_DCF' | 'UPLOAD_TO_DCF_PORTAL' | 'CHECK_NOCA_FLMMIS' | 'CORRECT_UMED' | 'PROCESS_CROSSOVER'
  | 'PROCESS_REVERSAL' | 'RESOLVE_PROBLEM_ACCOUNT' | 'HANDOFF_TO_FINANCE'
  | 'CREATE_UMED' | 'DCF_RESUB';
/** Why a UMED artifact must be produced (packet §8). Controlled; never free text. */
export type CreationReason = 'NEW' | 'SFS_CORRECTION' | 'PAID_IN_FULL_CORRECTION' | 'SFS_CORRECTION_FLMMIS_ISSUE' | 'REVERSAL' | 'REVERSAL_RESUB' | 'DCF_RESUB' | 'UNKNOWN';
export type CorrectionType = 'SFS_CORRECTION' | 'PAID_IN_FULL_CORRECTION' | 'SFS_CORRECTION_FLMMIS_ISSUE';

/** PAR 01 (2026-10-08): the word a partial approval writes. eMerge's own: Monday never had it, so its colour is eMerge's. */
export const PARTIAL_APPROVAL_WORD = 'APPROVED - SFS CORRECTION';
/** The legend's colour for it (readModel.STATUS_LEGEND): between APPROVED's gold and SFS CORRECTION's orange. */
export const PARTIAL_APPROVAL_HEX = '#e2a03f';
export interface PartialApproval {
  missing_months: string[] | null;
  approved_months: string[] | null;
  correction_id: string | null;
  correction_open: boolean;
}
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** '2026-09' -> 'Sep 2026'; several joined with commas. Anything not a month is shown as it is. */
export function monthsLabel(months: readonly string[] | null | undefined): string {
  return (months || []).map((m) => {
    const hit = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(String(m));
    return hit ? `${MONTH_SHORT[Number(hit[2]) - 1]} ${hit[1]}` : String(m);
  }).join(', ');
}
/** The native reversal (U-18): who asked, where it stands, what DCF said. */
export type ReversalRequester = 'FACILITY' | 'RESPONSIBLE_PARTY' | 'DCF' | 'SFS' | 'OTHER';
export type ReversalState = 'REQUESTED' | 'SUBMITTED' | 'NOT_APPROVED' | 'COMPLETED' | 'WITHDRAWN';
/** The DCF resubmittal's kind (2026-10-15): CORRECTED = the claim stands at corrected terms; FULL = unwound. */
export type ResubmittalKind = 'CORRECTED' | 'FULL';
/** Where a reversal / DCF resubmittal may start (Isaiah Cotto, 2026-10-29; resubmittalState.START_STATUSES). */
export type ReversalStartStatus = 'APPROVED' | 'DCF_SUBMITTED' | 'RESUB_DCF_SUBMITTED' | 'SFS_CORRECTION';
export type ReversalOutcome = 'APPROVED' | 'NOT_APPROVED';
/** The DCF account as eMerge holds it (packet §13): a state machine, not a checkbox. */
export type DcfNativeState = 'NEEDS_EMAIL' | 'EMAIL_ASSIGNED' | 'ACCOUNT_CREATED' | 'UNABLE_TO_CREATE' | 'NOT_REQUIRED' | 'UNKNOWN';
// CLOSED (PR #420): the case's current episode is a corrected re-send that was sent (by a settled fax) or cancelled.
export type ReadinessState = 'READY' | 'READY_UNVERIFIED' | 'NOT_READY' | 'REVIEW_REQUIRED' | 'CLOSED';
export type Requirement = 'UMED_DOCUMENT' | 'AUDIOGRAM' | 'DCF_ACCOUNT' | 'SSN' | 'STUCK' | 'CORRECTION_REASON' | 'ORIGINAL_LINEAGE' | 'EXCEPTION_OPEN';
export type NocaResult = 'NOT_RECORDED' | 'PENDING' | 'RECEIVED';
export type FlmmisResult = 'NOT_RECORDED' | 'NOT_REFLECTED' | 'REFLECTED';
export type ReviewDecision = 'CONTINUE' | 'RESUBMIT' | 'FULLY_APPROVED';
export type AttentionCode = 'STATUS_UNMAPPED' | 'OPERATOR_UNRESOLVED' | 'EXCEPTION' | 'MISSING_EVIDENCE' | 'FOLLOW_UP_OVERDUE' | 'APPROVAL_EVIDENCE_UNKNOWN';
export type Freshness = 'FRESH' | 'BEHIND' | 'STALE' | 'NEVER';
export type OwnerKind = 'ASSIGNED' | 'NO_ACCOUNT' | 'UNRESOLVED' | 'UNASSIGNED';
export type Handoff = 'COMPLETE' | 'MISSING' | 'NOT_ESTABLISHED' | 'NOT_APPLICABLE';

export type LensKey =
  | 'needs-creation' | 'needs-documents' | 'ready-to-submit' | 'exceptions'
  | 'waiting-on-evidence' | 'awaiting-approval' | 'reversal' | 'correction' | 'crossover'
  | 'approval-conflict' | 'everything'
  // The approval queues the server has offered since 2026-09-19. They were
  // rendered from `scope.lenses` but missing here, so choosing one wrote a
  // lens the client then refused to read back: Josie picked her NOCA queue
  // and landed on Needs creation. A lens the server offers must be a key the
  // client accepts (packet §1).
  | 'review-for-approval' | 'noca-flmmis-pending' | 'flmmis-correction'
  | 'board-ready-to-submit'
  // "Not qualifying / recert" (AP-1). The server has offered it since EP-010
  // and the client refused it the same way, so choosing it landed on Needs
  // creation. Dispositions (2026-10-02) are where these cases go.
  | 'suspended'
  // UM-R1 (2026-10-02): PROBLEM ACCOUNT left Review for approval and has a view of its own.
  | 'problem-account'
  // Gwynn, 2026-10-12: "(STUCK) Can't link Benefits", natively.
  | 'dcf-benefits-stuck'
  // APR 02 (2026-10-13): waiting for FLMMIS and the last check is 3 or more days old. Offered to approval reviewers only.
  | 'flmmis-check-due'
  // Every other lens in readModel LENSES (2026-10-13 triage of PR #370): the server offered these, the client refused them and fell back to Needs creation.
  // backend/src/services/umed/__tests__/uiKeysMatchServer.test.js pins this list against the server catalog.
  | 'umed-submitted' | 'dcf-account-needed' | 'ready-to-approve' | 'new-approvals-noca'
  | 'correction-completed' | 'sent-in-monday' | 'monday-not-applied' | 'after-approval' | 'revet-due';
const LENS_KEYS: readonly LensKey[] = [
  'needs-creation', 'needs-documents', 'ready-to-submit', 'exceptions',
  'waiting-on-evidence', 'awaiting-approval', 'reversal', 'correction', 'crossover', 'approval-conflict', 'everything',
  'review-for-approval', 'noca-flmmis-pending', 'flmmis-correction',
  'board-ready-to-submit', 'suspended', 'problem-account', 'dcf-benefits-stuck', 'flmmis-check-due',
  'umed-submitted', 'dcf-account-needed', 'ready-to-approve', 'new-approvals-noca',
  'correction-completed', 'sent-in-monday', 'monday-not-applied', 'after-approval', 'revet-due',
];

/**
 * The governed dispositions (2026-10-02). Recorded by the approval reviewer
 * from the board row; each writes one event with its reason, and REACTIVATE
 * is the way back. The server decides what each one writes and which queues
 * the case leaves; these words only name them.
 */
export type DispositionWord = 'NO_LONGER_QUALIFIES' | 'NEEDS_MEDICAID_RECERT' | 'DECEASED' | 'PAID_IN_FULL' | 'DCF_RESUBMISSION_NEEDED' | 'PROBLEM_ACCOUNT';
export type Disposition = DispositionWord | 'REACTIVATE';
export const DISPOSITION_WORDS: readonly DispositionWord[] = ['NO_LONGER_QUALIFIES', 'NEEDS_MEDICAID_RECERT', 'DECEASED', 'PAID_IN_FULL', 'DCF_RESUBMISSION_NEEDED', 'PROBLEM_ACCOUNT'];
export const DISPOSITION_LABEL: Record<Disposition, string> = {
  NO_LONGER_QUALIFIES: 'No longer qualifies',
  NEEDS_MEDICAID_RECERT: 'Needs Medicaid recert',
  DECEASED: 'Deceased',
  PAID_IN_FULL: 'Paid in full',
  DCF_RESUBMISSION_NEEDED: 'DCF resubmission needed',
  // UM-R1/UM-R2 (Josie Evans, 2026-10-02): the board's PROBLEM ACCOUNT, with the note for the PA as its reason.
  PROBLEM_ACCOUNT: 'Problem account',
  REACTIVATE: 'Reactivate',
};
/*
 * "Paid in full" (Dominique Genua, Finance, 2026-10-20: "We need the option paid in full, not every member will need a
 * UMED submitted, that's a rare case"): the three paid-in-full acts, ONE group on the UMED row's status menu, each
 * named and explained in the same words wherever it is offered. Each `key` is the act it runs, and each `effect` is
 * what that act really does on the server:
 *   PAID_IN_FULL               the PAID_IN_FULL disposition (nativeWorkflowService.dispositionChange): finance_state
 *                              PAID_IN_FULL, stage HANDED_TO_FINANCE when it was earlier, status word PAID IN FULL. It
 *                              does NOT close the case and does NOT open a Finance handoff -- said so, not promised.
 *   PAID_IN_FULL_UMED_PENDING  the "Paid in full, UMED pending" mark (#427, umed/paidInFull.markPaidInFull): an event
 *                              only; the case keeps its stage, word and lists; Finance reads the mark (board row and
 *                              dossier) and, once the UMED is approved, its handoff says "reimbursement owed".
 *   PAID_IN_FULL_CORRECTION    requestCorrection with correction_type PAID_IN_FULL_CORRECTION: the same act, rules
 *                              and packet flow as an SFS correction (one open correction per case).
 * Exported so the Finance workspace can name them the same way.
 */
export type PaidInFullOptionKey = 'PAID_IN_FULL' | 'PAID_IN_FULL_UMED_PENDING' | 'PAID_IN_FULL_CORRECTION';
export interface PaidInFullOption { key: PaidInFullOptionKey; label: string; effect: string }
export const PAID_IN_FULL_GROUP_LABEL = 'Paid in full';
export const PAID_IN_FULL_OPTIONS: readonly PaidInFullOption[] = Object.freeze([
  {
    key: 'PAID_IN_FULL',
    label: 'Paid in full',
    effect: 'Resident paid. No UMED needed. The case leaves the approval chase and its status becomes PAID IN FULL. It is not closed, and no Finance handoff is opened. On a case DCF has not approved it will show under Approval evidence incomplete until it is approved.',
  },
  {
    key: 'PAID_IN_FULL_UMED_PENDING',
    label: 'Paid in full · UMED pending',
    effect: 'Resident paid. The UMED carries on through submission and approval; Finance is flagged.',
  },
  {
    key: 'PAID_IN_FULL_CORRECTION',
    label: 'Paid in full correction',
    effect: 'Resident paid. Opens a correction on this case so DCF is sent the corrected UMED.',
  },
] as PaidInFullOption[]);
export const paidInFullOption = (key: PaidInFullOptionKey): PaidInFullOption =>
  PAID_IN_FULL_OPTIONS.find((o) => o.key === key) as PaidInFullOption;
/**
 * The NOCA as evidence (UM-R3, 2026-10-02): reviewed, on file and not yet
 * reviewed, or neither. A document on file is never shown as "Not recorded"
 * and never counted as a review.
 */
export type NocaEvidence = 'RECEIVED' | 'DOCUMENT_ON_FILE' | 'NOT_RECORDED' | 'REJECTED' | 'WITHHELD';
/** What the case carries, from the server: the word, who recorded it, and whether REACTIVATE will be accepted. */
export interface CaseDisposition { word: DispositionWord; label: string; source: 'EMERGE' | 'MONDAY'; reactivatable: boolean }
/** A lens the server still offers. A retired key in an old link (`needs-dcf-account`, April 2026-09-18) is not one. */
export const isLensKey = (k: string | null): k is LensKey => k !== null && (LENS_KEYS as readonly string[]).includes(k);
/**
 * The DCF account as a requirement of the work (April, 2026-09-18): a new
 * UMED needs a new account; a correction, reversal or resubmission reuses the
 * resident's existing one. Derived by the server from the records — this
 * case's, the resident's other UMEDs, the board — never assumed.
 */
export type DcfNeed = 'NEEDED' | 'PROVISIONED' | 'ISSUE' | 'NOT_REQUIRED' | 'UNKNOWN';
export type ExceptionKey = 'reversal' | 'correction' | 'resubmission' | 'crossover' | 'approval-conflict' | 'problem' | 'unmapped' | 'operator-unresolved';

/**
 * Monday calls the case approved and nobody approved it here: ONE name for that
 * claim on every screen and in the export (final verification 2026-10-06, P3) --
 * the stage, the Finance word, the chip's tooltip and screen-reader text.
 */
export const MONDAY_APPROVAL_CLAIM = 'Approved in Monday · not approved in eMerge';
/** The stage in the business's words. Not the board's group, and not a code. */
export const STAGE_LABEL: Record<Stage, string> = {
  CREATED: 'Created', NEEDS_DCF_ACCOUNT: 'Needs DCF account', READY: 'Ready to submit', SUBMITTED: 'Submitted to DCF',
  APPROVED: MONDAY_APPROVAL_CLAIM, HANDED_TO_FINANCE: 'With Finance', CLOSED: 'Closed', UNKNOWN: 'Status not recognised',
};
/**
 * The stage in words, for THIS case (2026-10-06, P2-6). A case only reaches
 * APPROVED in eMerge through the approval act, so an act-approved case's stage
 * is "Approved"; the same stage without the act is the board's, and says so.
 * Monday's With Finance without the act is the same claim (the server's
 * effective state reads both as APPROVED_AT_SOURCE; the export follows this
 * rule, effectiveState.stageLabelOf).
 */
export function stageLabelOf(stage: Stage, approvedByAct: boolean): string {
  if (stage === 'APPROVED' && approvedByAct) return 'Approved';
  if (stage === 'HANDED_TO_FINANCE' && !approvedByAct) return MONDAY_APPROVAL_CLAIM;
  return STAGE_LABEL[stage];
}
/** What the approval word says, for a screen outside the UMED board (Finance). */
export type ApprovalWord = 'APPROVED' | 'FLMMIS_REFLECTED' | 'APPROVED_AT_SOURCE' | 'NOT_APPROVED';
export const APPROVAL_WORD_LABEL: Record<ApprovalWord, string> = {
  APPROVED: 'Approved in eMerge',
  FLMMIS_REFLECTED: 'FLMMIS reflected · not approved',
  APPROVED_AT_SOURCE: MONDAY_APPROVAL_CLAIM,
  NOT_APPROVED: 'Not approved',
};
/**
 * Approved means the act, everywhere (final verification 2026-10-06, P2-C):
 * a UMED operator moved the case to Approved in eMerge. FLMMIS reflected is
 * the evidence the act needs, not the act; Monday's approved stage (or a
 * Finance word on the board) is Monday's claim, not eMerge's. The same order
 * as the case's own effective state: the act, then the evidence, then the
 * board's claim.
 */
export function approvalWordOf(a: { proven: boolean; flmmis?: string | null; stage?: string | null; finance_state?: string | null }): ApprovalWord {
  if (a.proven) return 'APPROVED';
  if (a.flmmis === 'REFLECTED') return 'FLMMIS_REFLECTED';
  if (a.stage === 'APPROVED' || a.stage === 'HANDED_TO_FINANCE' || (a.finance_state && a.finance_state !== 'NOT_OPEN')) return 'APPROVED_AT_SOURCE';
  return 'NOT_APPROVED';
}
export const UMED_TYPE_LABEL: Record<UmedType, string> = {
  NEW: 'New UMED', REPLACEMENT: 'Replacement', CORRECTION: 'Correction', CROSSOVER: 'Crossover', REVERSAL: 'Reversal', UNKNOWN: 'Type not recorded',
};
export const EXCEPTION_LABEL: Record<Exclude<ExceptionKind, 'NONE'>, string> = {
  CORRECTION: 'Correction', CROSSOVER: 'Crossover', REVERSAL: 'Reversal', PROBLEM: 'Problem account', RESUBMISSION: 'DCF resubmission',
};
/**
 * Whether a screen names the case's open exception (Josie Evans, CORRECTIONS, 2026-10-28, R7): "a resubmission is when
 * we have not seen ANY type of approval from DCF". A RESUBMISSION left open on a case approved here, or with a
 * correction recorded here open, is not named -- the server's own guard (readModel.RESUBMISSION_SHOWN).
 */
export function exceptionShown(c: Pick<UmedCase, 'exception' | 'exception_open'> & { correction?: OpenCorrection | null; approval?: { canonical?: string } | null }): boolean {
  if (!c.exception_open || c.exception === 'NONE') return false;
  if (c.exception !== 'RESUBMISSION') return true;
  return !(c.approval && c.approval.canonical === 'PROVEN') && !c.correction;
}
/** What is being produced or sent, and why — the row's first line under Needs creation and Ready to submit. */
export const CREATION_REASON_LABEL: Record<CreationReason, string> = {
  NEW: 'New UMED', SFS_CORRECTION: 'SFS correction', PAID_IN_FULL_CORRECTION: 'Paid-in-full correction',
  SFS_CORRECTION_FLMMIS_ISSUE: 'SFS correction · FLMMIS issue', REVERSAL: 'Reversal', REVERSAL_RESUB: 'Reversal resubmission',
  DCF_RESUB: 'DCF resubmission', UNKNOWN: 'Reason needs review',
};
export const CORRECTION_TYPE_LABEL: Record<CorrectionType, string> = {
  SFS_CORRECTION: 'SFS correction', PAID_IN_FULL_CORRECTION: 'Paid-in-full correction', SFS_CORRECTION_FLMMIS_ISSUE: 'SFS correction · FLMMIS issue',
};
export const REVERSAL_REQUESTER_LABEL: Record<ReversalRequester, string> = {
  FACILITY: 'The facility', RESPONSIBLE_PARTY: 'The responsible party', DCF: 'DCF', SFS: 'SFS', OTHER: 'Other',
};
/** The DCF resubmittal's state in words. REQUESTED splits by whether Finance has handed it to UMED (see reversalStateWords). */
export const REVERSAL_STATE_LABEL: Record<ReversalState, string> = {
  REQUESTED: 'With UMED · packet not yet sent', SUBMITTED: 'With DCF', NOT_APPROVED: 'DCF declined', COMPLETED: 'DCF approved', WITHDRAWN: 'Withdrawn',
};
export const RESUBMITTAL_WITH_FINANCE_LABEL = 'With Finance';
/** A resubmittal's state in words; a REQUESTED one is With Finance until it is handed to UMED. */
export const reversalStateWords = (state: ReversalState, handed: boolean): string =>
  (state === 'REQUESTED' && !handed ? RESUBMITTAL_WITH_FINANCE_LABEL : REVERSAL_STATE_LABEL[state]);
export const FINANCE_STATE_LABEL: Record<FinanceState, string> = {
  NOT_OPEN: 'not open', PORTFOLIO: 'in the SFS portfolio', PAID_IN_FULL: 'paid in full', PAID_IN_FULL_CORRECTION: 'paid in full (corrected)',
};
export const DCF_NEED_LABEL: Record<DcfNeed, string> = {
  NEEDED: 'Needed', PROVISIONED: 'Provisioned', ISSUE: 'Could not be created', NOT_REQUIRED: 'Not needed', UNKNOWN: 'Not recorded',
};
export const DCF_NATIVE_LABEL: Record<DcfNativeState, string> = {
  NEEDS_EMAIL: 'Needs an email', EMAIL_ASSIGNED: 'Email assigned · account not yet created', ACCOUNT_CREATED: 'Account created',
  UNABLE_TO_CREATE: 'Unable to create', NOT_REQUIRED: 'Not needed', UNKNOWN: 'Not recorded',
};
export const REQUIREMENT_LABEL: Record<Requirement, string> = {
  UMED_DOCUMENT: 'UMED', AUDIOGRAM: 'Audiogram', DCF_ACCOUNT: 'DCF account', SSN: 'SSN', STUCK: 'Marked stuck on the board',
  CORRECTION_REASON: 'Correction reason', ORIGINAL_LINEAGE: 'Original UMED', EXCEPTION_OPEN: 'An open exception',
};
export const NOCA_RESULT_LABEL: Record<NocaResult, string> = { NOT_RECORDED: 'Not recorded', PENDING: 'Pending', RECEIVED: 'Received' };
export const FLMMIS_RESULT_LABEL: Record<FlmmisResult, string> = { NOT_RECORDED: 'Not recorded', NOT_REFLECTED: 'Not reflected', REFLECTED: 'Reflected' };
export const DECISION_LABEL: Record<ReviewDecision, string> = { CONTINUE: 'Review again in ten days', RESUBMIT: 'Send back for DCF resubmission', FULLY_APPROVED: 'Fully approved' };
export const EXCEPTION_STATE_LABEL: Record<ExceptionState, string> = {
  OPEN: 'open', RESUBMITTED: 'resubmitted', NOT_APPROVED: 'not approved', COMPLETE: 'complete',
};
export const LIFECYCLE_LABEL: Record<Lifecycle, string> = {
  DECEASED: 'Deceased', NO_LONGER_QUALIFIES: 'No longer qualifies', NEEDS_RECERT: 'Needs Medicaid recertification',
};
export const BLOCKER_LABEL: Record<Blocker, string> = {
  SSN_REQUIREMENT: 'Needs SSN', SIGNED_UMED: 'Needs a signed UMED', NEW_UMED: 'Needs a new UMED',
  AUDIOGRAM: 'Needs an audiogram', STUCK: 'Marked stuck',
};
export const ACTION_LABEL: Record<ActionType, string> = {
  CREATE_DCF_ACCOUNT: 'Create the DCF account', OBTAIN_READINESS_EVIDENCE: 'Get the missing evidence',
  GENERATE_UMED: 'Generate the UMED', VERIFY_UMED: 'Verify the UMED', ASSEMBLE_PACKET: 'Assemble the packet',
  SUBMIT_TO_DCF: 'Submit to DCF', UPLOAD_TO_DCF_PORTAL: 'Upload to the DCF portal',
  CHECK_NOCA_FLMMIS: 'Check NOCA and FLMMIS', CORRECT_UMED: 'Correct the UMED', PROCESS_CROSSOVER: 'Work the crossover',
  PROCESS_REVERSAL: 'Work the DCF resubmittal', RESOLVE_PROBLEM_ACCOUNT: 'Resolve the problem account',
  HANDOFF_TO_FINANCE: 'Hand off to Finance',
  CREATE_UMED: 'Create the UMED', DCF_RESUB: 'Resubmit to DCF',
};
/**
 * Attention, in the business's words (April Torino, 2026-09-11: "What does
 * exception mean? Approval unproven?"). The canonical codes stay precise
 * underneath; what a person reads names the actual condition. An EXCEPTION
 * says which work is required; MISSING_EVIDENCE says which document.
 */
export const ATTENTION_LABEL: Record<AttentionCode, string> = {
  STATUS_UNMAPPED: 'Status not recognised', OPERATOR_UNRESOLVED: 'Owner not on the roster', EXCEPTION: 'Work required',
  MISSING_EVIDENCE: 'Missing document', FOLLOW_UP_OVERDUE: 'Review overdue', APPROVAL_EVIDENCE_UNKNOWN: 'Source says approved · verification incomplete',
};
export const EXCEPTION_WORK_LABEL: Record<Exclude<ExceptionKind, 'NONE'>, string> = {
  REVERSAL: 'Reversal required', CORRECTION: 'Correction required', CROSSOVER: 'NOCA received, FLMMIS not reflected',
  PROBLEM: 'Problem account', RESUBMISSION: 'DCF resubmission required',
};
/** How a packet reached DCF. Recorded with the sending (packet §24). */
export type SubmissionMethod = 'FAX' | 'PORTAL_UPLOAD' | 'MAIL' | 'OTHER';
export const SUBMISSION_METHOD_LABEL: Record<SubmissionMethod, string> = { FAX: 'Faxed', PORTAL_UPLOAD: 'Uploaded to the DCF portal', MAIL: 'Mailed', OTHER: 'Other' };

/**
 * The two approval facts, said exactly as strongly as the data supports.
 * Pending is not rejected. Not recorded is not "not received".
 */
export const NOCA_LABEL: Record<Noca, string> = {
  RECEIVED: 'Received', PENDING: 'Pending', REJECTED: 'Rejected', UNKNOWN: 'Not recorded', WITHHELD: 'Not shown to you',
};
export const FLMMIS_LABEL: Record<Flmmis, string> = {
  REFLECTED: 'Reflected', NOT_REFLECTED: 'Not reflected', EXCEPTION: 'Exception', UNKNOWN: 'Not recorded', WITHHELD: 'Not shown to you',
};
export const DCF_ACCOUNT_LABEL: Record<DcfAccount, string> = {
  REQUIRED: 'Needed, not created', NOT_REQUIRED: 'Not needed', PENDING: 'Requested', READY: 'Ready',
  BLOCKED: 'Blocked', UNKNOWN: 'Not recorded',
};
export const HANDOFF_LABEL: Record<Handoff, string> = {
  COMPLETE: 'Handed to Finance', MISSING: 'Not handed to Finance', NOT_ESTABLISHED: 'Not established',
  NOT_APPLICABLE: 'Not yet',
};

export interface OpenAction { id: string; action_type: ActionType; due_on: string | null; reason: string | null; owner_user_id: string | null;
  /** 2026-10-14: the reason named NOCA or FLMMIS and this caller has no approval detail, so it was withheld (reason null). */
  reason_withheld?: boolean }
export interface Attention { code: AttentionCode; detail: string }
export interface Owner { kind: OwnerKind; name: string | null }
/** `APPROVAL` (2026-10-06): FLMMIS is in evidence and nobody has moved the case to Approved yet. */
export interface MissingFact { fact: 'NOCA' | 'FLMMIS' | 'APPROVAL'; state: Noca | Flmmis | 'NOT_RECORDED' }

/**
 * The one state a row may claim, decided by the server
 * (services/umed/effectiveState.js). Current verified evidence outranks the
 * board word that came with the case, so "Fully approved" and "Correction
 * required" can no longer appear on the same row.
 */
export type EffectiveStateKey =
  // APPROVED_AT_SOURCE (2026-10-06, P1-1): Monday's APPROVED stage with no approval act -- never "Fully approved".
  | 'CORRECTION_REQUIRED' | 'REVERSAL_OPEN' | 'EXCEPTION_OPEN' | 'APPROVED' | 'APPROVED_AT_SOURCE' | 'READY_TO_APPROVE'
  | 'NOCA_RECEIVED_FLMMIS_PENDING' | 'SUBMITTED' | 'READY_TO_SUBMIT' | 'IN_CREATION' | 'CLOSED' | 'UNKNOWN';

export interface EffectiveState {
  state: EffectiveStateKey;
  label: string;
  proven: boolean;
  noca: Noca;
  flmmis: Flmmis;
  approved_at: string | null;
  correction_open: boolean;
  /** Kept and shown as history, never as the current state. */
  provenance: { source_status_raw: string | null; superseded_exception: string | null; superseded_by: string | null };
}

/** Where Finance stands on an approved case. MISSING is an exception somebody owns. */
export interface FinanceHandoff { handoff: Handoff | 'PENDING'; state: string | null; account_exists: boolean }

export const FINANCE_HANDOFF_LABEL: Record<string, string> = {
  COMPLETE: 'Finance handoff complete',
  PENDING: 'Finance handoff pending',
  MISSING: 'Finance handoff missing',
  NOT_ESTABLISHED: 'Finance not established',
  NOT_APPLICABLE: '',
};

/**
 * The approval block. Four facts kept apart on purpose: what the source
 * claims, each evidence state on its own, and the verdict. `canonical` is
 * PROVEN only when FLMMIS is reflected, whatever the board says (U-16:
 * FLMMIS is what lets the facility bill; NOCA is evidence, not a gate).
 * `basis` says whether a proven approval rests on both facts or on FLMMIS
 * alone; it is null when not proven and absent when the facts are withheld.
 */
export type ApprovalBasis = 'FLMMIS_AND_NOCA' | 'FLMMIS_ONLY';
export interface OtherUmed {
  stage: string;
  word: string | null;
  source: 'MONDAY' | 'EMERGE';
  submitted_on: string | null;
  sent_to_dcf_on: string | null;
}

export interface Approval {
  noca: Noca;
  flmmis: Flmmis;
  canonical: 'PROVEN' | 'NOT_PROVEN';
  basis?: ApprovalBasis | null;
  source_says_approved: boolean;
  source_status_raw: string | null;
  conflict: boolean;
  missing: MissingFact[];
  /**
   * 2026-10-06: approval is the operator's act. 2026-10-13: NOCA received AND FLMMIS reflected are on record and
   * nobody has moved the case to Approved yet, so the Approve act is offered. Optional for a payload from before the deploy.
   */
  ready_to_approve?: boolean;
  /**
   * 2026-10-06: approved, the NOCA not in yet, and in this viewer's approval scope: "Record NOCA received" is
   * offered (POST /noca-received). A review is refused on an approved case, so this is the NOCA's only way in.
   */
  late_noca_recordable?: boolean;
  /**
   * 2026-10-10: this viewer's approval scope covers the case's building -- the same predicate as ready_to_approve,
   * said before FLMMIS is reflected. Read-only; it decides what the row offers, never what is allowed. A payload
   * from before the deploy has no field, which the board reads as NOT in scope.
   */
  in_approval_scope?: boolean;
  /** Who moved it to Approved, and when. Null until somebody does. */
  approved_by_name?: string | null;
  approved_at?: string | null;
  /** MONDAY: the two facts mirror the board. EMERGE: a review recorded them natively. */
  source?: 'MONDAY' | 'EMERGE';
}
/** What eMerge can say about sending this case (packet §10): proven missing, not yet trackable, or ready. */
/**
 * The Monday item behind "Monday says" (final verification P2-3): its id, the board's own sending dates, when
 * eMerge first saw it, and which arm made it this work's -- the case's own row, first seen after the case
 * opened, or ONLY the board's sending date (an older fitting re-dated after a replacement opened reads so).
 */
export interface MondayItem {
  monday_item_id: string | null; sent_to_dcf_date: string | null; submitted_date: string | null; first_seen_at: string | null;
  /** The New York business day of first_seen_at, as the board counts days. */
  first_seen_on?: string | null;
  matched_by: 'CASE_ROW' | 'FIRST_SEEN' | 'SENDING_DATE' | null;
}
/**
 * `purpose_from` (final verification P2-1): what the sending is for comes from an open native REVERSAL or
 * CORRECTION when there is one, else from the CASE's creation reason. Absent on a payload from before the deploy.
 */
export interface Readiness { state: ReadinessState; purpose: CreationReason; purpose_from?: 'REVERSAL' | 'CORRECTION' | 'CASE'; missing: Requirement[]; untracked: Requirement[]; closed?: 'SENT' | 'CANCELLED' | 'WITHDRAWN' }
export interface ReviewClock { last_reviewed_at: string | null; last_reviewed_on: string | null; last_reviewed_source: 'EMERGE' | 'MONDAY' | null; next_review_on: string | null }
export interface OpenCorrection { id: string; correction_type: CorrectionType; state: 'REQUESTED' | 'SUBMITTED' | 'COMPLETED'; original_case_id: string | null; has_reason: boolean;
  /** When its corrected UMED last went to DCF (state SUBMITTED; Josie Evans, CORRECTIONS, 2026-10-28). */
  submitted_at?: string | null;
  /** "Still not reflecting -- send the correction again" (2026-10-28): how many times it was asked to be sent again. */
  resends?: number;
  /** Its corrected UMED went to DCF since it was opened or last asked again (SUBMITTED, or a sending #532 never marked). */
  sent_since_opened?: boolean;
  /** The note for Gwynn asked with it (2026-10-29); null when none was written, or withheld (`note_withheld`) where it names NOCA or FLMMIS. */
  note?: string | null;
  note_withheld?: boolean }
/**
 * The open correction in words (readModel.correctionStatusOf, 2026-10-28): its kind, and "open, to send" until its
 * corrected UMED went to DCF, then "sent, awaiting review". `replaces_word`: a paid in full correction requested before
 * then wears SFS CORRECTION, and this label is shown in the word's place (director's ruling 1).
 */
export interface CorrectionStatus { correction_type: CorrectionType; state: 'TO_SEND' | 'SENT'; label: string; submitted_at: string | null; replaces_word: boolean }
export interface OpenReversal { id: string; state: ReversalState; requester: ReversalRequester; original_case_id: string | null; submission_count: number; outcome: ReversalOutcome | null; has_reason: boolean; kind?: ResubmittalKind | null; handed?: boolean }
/** Absent entirely without UMED_FINANCE_HANDOFF_VIEW — never null, which would read as "no handoff". */
export interface Finance { handoff: Handoff; account_exists: boolean }

export interface UmedCase {
  id: string;
  patient_id: string | null;
  facility_id: string | null;
  facility_name: string | null;
  /** The resident's CounselEAR Preferred Clinic, sent when eMerge has no building (2026-10-14). */
  counselear_clinic_id?: string | null;
  counselear_clinic_name?: string | null;
  umed_type: UmedType;
  stage: Stage;
  exception: ExceptionKind;
  exception_state: ExceptionState | null;
  exception_open: boolean;
  lifecycle: Lifecycle | null;
  finance_state: FinanceState;
  dcf_account_state: DcfAccount;
  readiness_blockers: Blocker[];
  source_status_raw: string | null;
  source_faxed_status_raw: string | null;
  /** The board's own terms sentence. Quoted, never parsed (Josie, 2026-09-24). */
  source_terms_raw?: string | null;
  /** CounselEAR's "Employee ID". Entered in eMerge; CounselEAR does not expose it. */
  medicaid_number?: string | null;
  /**
   * The same resident's OTHER open UMED cases. Josie Evans, 2026-09-28:
   * "This patient is in here twice. Not sure if he's been submitted or not."
   */
  other_open_umeds?: OtherUmed[] | null;
  /** When eMerge opened the case. The only age a handoff nobody has touched has. */
  case_created_at?: string | null;
  /** EMERGE when eMerge opened the case itself — a Patient Advocate's handoff. */
  source_system?: 'MONDAY' | 'EMERGE';
  vocabulary_version: number;
  submitted_on: string | null;
  sent_to_dcf_on: string | null;
  /** Approved in eMerge: the act's date. Not approved: the board's date, a source claim (2026-10-06, P2-3). */
  approved_on: string | null;
  /** The board's date an approval act replaced, kept as provenance. Null when they agree or there is no act. */
  board_approved_on?: string | null;
  last_follow_up_on: string | null;
  closed_on: string | null;
  source_updated_at: string | null;
  first_name: string | null;
  last_name: string | null;
  open_actions: OpenAction[];
  next_action: OpenAction | null;
  /**
   * Monday's word for this work implies a later stage than eMerge's (Josie Evans, 2026-10-06). Shown, never
   * applied; `can_mark_submitted` says the one-click "Mark submitted to DCF" act will be accepted.
   */
  monday_differs?: { word: string; stage: Stage; can_mark_submitted: boolean; item?: MondayItem | null } | null;
  /** Monday's word for the same work when it is not eMerge's word and the stage marker has nothing to say (2026-10-06). Shown, never applied. */
  monday_word_differs?: { word: string } | null;
  /** #363 round 3: this case and another open case of the resident, one from a released Monday link. Said on both; closed by nobody. */
  two_open_after_relink?: boolean;
  /**
   * 2026-10-19: at HANDED_TO_FINANCE only because eMerge recorded it paid in full before any approval -- the DCF
   * resubmittal start refuses it (the server's one predicate, resubmittalState.PIF_NEVER_APPROVED_SQL).
   */
  resubmittal_pif_only?: boolean;
  /**
   * 2026-10-29 (Isaiah Cotto, amending REV 01): the status a reversal / DCF resubmittal would start as -- APPROVED,
   * DCF_SUBMITTED, RESUB_DCF_SUBMITTED or SFS_CORRECTION -- or null when the start refuses this case. The server's one
   * rule (resubmittalState.START_STATUS_SQL), the same as the start's gate. Absent from an older server.
   */
  reversal_start_status?: ReversalStartStatus | null;
  /** 2026-10-29: a FULL reversal withdrew this claim since its last sending (the start refuses CLAIM_ALREADY_WITHDRAWN). Absent from an older server. */
  reversal_claim_withdrawn?: boolean;
  /** 2026-10-29: eMerge's own fax of this case is in flight or being recorded (the server's FAX_IN_FLIGHT predicate). Absent on an older server. */
  fax_in_flight?: boolean;
  /** A DCF RESUB NEEDED move made in eMerge is in force (the submitter was told). False where the word is only Monday's or an older review's. */
  dcf_resub_moved?: boolean;
  /** Gwynn, 2026-10-12: the DCF benefits link as recorded here -- STUCK (with the reason) or CLEARED; null when never recorded. */
  dcf_benefits?: DcfBenefits | null;
  /** "Paid in full, UMED pending" (2026-10-15): a fact on the case, never a stage or a finance state. */
  paid_in_full_mark?: PaidInFullMark | null;
  /** The case's first sending (2026-10-15): the one its Sent to DCF day is, so the day can be corrected from the row. */
  first_submission_id?: string | null;
  /** Josie Evans, 2026-10-21: how many times a DCF resubmittal has been re-sent (a corrected re-send after a wrong document is not one), and the last ten days, newest last. */
  dcf_resend_count?: number;
  dcf_resend_dates?: string[];
  /** Josie Evans, R6 (2026-10-28): the day of the case's LAST NEW / DCF_RESUB sending (a corrected day honoured), null when none; sent_to_dcf_on stays the FIRST sending's. */
  last_sending_day?: string | null;
  /** Whether a DCF resubmission can be opened here (the move, or a review's RESUBMIT), and why not. FLMMIS is the caller's to judge. */
  resubmission?: { can_open: boolean; why: string | null; flmmis_only?: boolean };
  /** The one state this row may claim, and where Finance stands on it. */
  effective_state: EffectiveState;
  finance_handoff: FinanceHandoff;
  attention: Attention[];
  approval: Approval;
  owner: Owner;
  /** EP-005: eMerge-created work nobody has taken yet; any operator may claim it. */
  claimable?: boolean;
  finance?: Finance;
  /** Phase 3. Optional so a payload from before the deploy still renders. */
  creation_reason?: CreationReason | null;
  /** `need` · `email` · `existing_case_id` arrived 2026-09-19; optional so an older payload still renders. Never the password. */
  dcf_account?: { state: DcfNativeState; source: 'EMERGE' | 'MONDAY'; need?: DcfNeed; email?: string | null; existing_case_id?: string | null; revealable?: boolean };
  readiness?: Readiness;
  /** What eMerge holds of the two documents a packet depends on: true / false / null = not tracked yet. */
  /**
   * What eMerge HOLDS of the case's documents: true, false, or null where the
   * class has never been traversed. `noca` added 2026-09-28 so the NOCA column
   * can say whether the notice itself is in hand, not only what was typed.
   */
  /** `flmmis` (round 4, 2026-10-07): FLMMIS evidence attached to THIS case; null when the viewer may not see approval detail. */
  documents?: { umed: boolean | null; audiogram: boolean | null; noca?: boolean | null; flmmis?: boolean | null };
  /**
   * The file behind each icon of the NOCA / FLMMIS column (2026-10-21), gated as the flag beside it: the NOCA's id where the
   * NOCA is held, the FLMMIS's only for a viewer who may see approval detail. Opened with documentUrl, which needs the
   * document capability (the screen shows no link without it).
   */
  noca_document_id?: string | null;
  flmmis_document_id?: string | null;
  /** The current VALID packet version, when one has been generated and not yet sent. */
  packet?: { id: string; version: number; purpose: CreationReason; generated_at: string } | null;
  /** P0 2026-10-07: this episode's automatic packet preparation; null when none was requested. */
  packet_preparation?: PacketPreparationRow | null;
  review?: ReviewClock;
  correction?: OpenCorrection | null;
  /** The open correction's kind and state in words (R4, 2026-10-28). Null without one; absent on an older payload. */
  correction_status?: CorrectionStatus | null;
  /**
   * PAR 01 (Kaleigh Vazquez & Josie Evans, 2026-10-08): the case was approved for the months FLMMIS reflects
   * (APPROVED - SFS CORRECTION). `missing_months` ('YYYY-MM') wait on the SFS correction it opened; `correction_open`
   * until Josie's APPROVED completes it. Null or absent on every other case.
   */
  partial_approval?: PartialApproval | null;
  reversal?: OpenReversal | null;
  /** eMerge holds a recorded sending for this case's current creation reason. */
  submission_recorded?: boolean;
  /** Who moves the case forward: the board (MONDAY) or eMerge. */
  forward_authority?: 'MONDAY' | 'EMERGE';
  /** The disposition word, when the case carries one (2026-10-02). Null or absent on almost every case. */
  disposition?: CaseDisposition | null;
  /** UM-R3: whether eMerge holds the NOCA, and whether anybody has recorded reading it. */
  noca_evidence?: NocaEvidence;
  /** Processes 5 and 6: whether the after-approval acts are offered, and what is open. Null/absent where the server sends none. */
  after_approval?: AfterApproval | null;
  /** APR 02: whether FLMMIS is awaited, the Florida day it was last checked and whether a check is due. Null where withheld. */
  flmmis_check?: { waiting: boolean; last_checked_on: string | null; due: boolean; due_since: string | null } | null;
}

/** An open PL correction (Process 5): the new PL in cents and the month it takes effect ('YYYY-MM-01'). */
export interface OpenPlCorrection {
  id: string; state: 'OPEN'; new_pl_cents: number; effective_month: string; reason: string; recorded_at: string; recorded_by_name: string | null;
  correction_id: string; correction_state: string;
}
/** An open Medicaid-benefits-lost re-vet (Process 6): due `revet_due_on`, owned by the PA covering the building. */
export interface OpenBenefitsLost {
  id: string; state: 'OPEN'; effective_on: string; revet_due_on: string; revet_days: number;
  revet_owner_user_id: string | null; revet_owner_name: string | null; reason: string; recorded_at: string; recorded_by_name: string | null;
  revet_visible: boolean;
}
export interface AfterApproval { eligible: boolean; pl_correction: OpenPlCorrection | null; benefits_lost: OpenBenefitsLost | null }

export interface FollowUp extends UmedCase {
  action_id: string;
  action_type: ActionType;
  due_on: string | null;
  reason: string | null;
  action_created_at: string;
  past_due: boolean;
  days_late: number;
}

export interface Paged<T> { items: T[]; total: number; page: number; limit: number }
export interface WorkPage extends Paged<UmedCase> { lens: LensKey }
export interface FollowUpPage extends Paged<FollowUp> { recheck_days: number }
/** `source_claimed` is why an empty Approved list is not an empty database. */
export interface ApprovedPage extends Paged<UmedCase> { source_claimed: number; finance_missing: number }
export interface ExceptionPage extends Paged<UmedCase> { kind: ExceptionKey; what: string }

export interface CaseAction extends OpenAction {
  status: 'OPEN' | 'DONE' | 'CANCELLED';
  source_rule: string; rule_version: number; completed_at: string | null; created_at: string;
  /** PR #416: an owed fax or upload of a revision carrying a document marked "Not this resident's": open, not to be done. */
  blocked_for?: 'WRONG_DOCUMENT_SENT' | null;
}
export interface CaseEvent { id: string; event_type: string; source_system: string; source_rule: string | null; occurred_at: string; actor_name: string | null }
export interface FacilityRef { id: string; name: string | null; region: string | null; county: string | null; phone: string | null }

/**
 * "(STUCK) Can't link benefits" (Gwynn, 2026-10-12): the DCF account exists, but the resident's benefits cannot be linked
 * to it, so the UMED cannot be uploaded. The newer of the case's two benefits events; `reason` only while stuck.
 */
export interface DcfBenefits { state: 'STUCK' | 'CLEARED'; reason: string | null; note: string | null; recorded_at: string | null; recorded_by_name: string | null }
/**
 * Where an act chosen on the board continues in the case panel (Gwynn, 2026-10-12): UMED UPLOADED TO DCF opens the
 * packet's Confirm DCF upload; DCF EMAIL CREATED opens the DCF account's record form. `n` is fresh per ask.
 * An approval on the board opens the approval email (2026-10-13, ApprovalEmailPanel). UMED SUBMITTED TO DCF opens the
 * sending chooser (choose-sending); FAX TO DCF and the chooser's fax choices focus the packet panel's fax actions.
 */
export interface CaseFocus { kind: 'confirm-upload' | 'dcf-email' | 'approval-email' | 'choose-sending' | 'fax-dcf' | 'record-fax'
  // The Board layout (2026-10-13): a drop on Approved opens Approve with its confirmation; a refused move offers the
  // packet section or the approval evidence (caseAnchor.ts).
  | 'approve' | 'packet' | 'approval-evidence'
  // 2026-10-28: the row's "Record the review" (no APPROVED tile until NOCA received and FLMMIS reflected are on record).
  | 'review'
  // 2026-10-28: the row's "Record the terms" when an open correction's packet is held for its terms or total.
  | 'terms'; n: number }
export const DCF_BENEFITS_STUCK_LABEL = '(STUCK) Can’t link benefits';
/**
 * The sending purposes whose work needs a DCF account: the server's readiness.REASONS_NEEDING_ACCOUNT (the reasons whose
 * REQUIREMENTS_BY_REASON include DCF_ACCOUNT). For any other purpose -- a correction, a reversal, UNKNOWN -- the server's
 * gates never look at the account (packet/preparation.dcfReady), so no screen may say the account holds or blocks it
 * (Copilot on #403: a correction on a case whose NEW episode left UNABLE_TO_CREATE was told "it cannot be uploaded").
 */
export const PURPOSES_NEEDING_DCF_ACCOUNT: ReadonlyArray<string> = Object.freeze(['NEW', 'DCF_RESUB']);
/** Does work for this purpose need the DCF account? An unnamed purpose is a plain sending (NEW), as the server reads it. */
export const purposeNeedsDcfAccount = (purpose: string | null | undefined): boolean => !purpose || PURPOSES_NEEDING_DCF_ACCOUNT.includes(purpose);
/** The stuck act's answer: the mark, and what it did to the case's DCF account (STUCK = account made, 2026-10-13). */
export interface BenefitsStuckResult {
  state: 'STUCK'; reason: string; changed: boolean; replayed: boolean;
  account?: { recorded: true; state: 'ACCOUNT_CREATED'; stageAdvanced?: boolean } | { recorded: false; why: string | null };
}

/** The DCF account record — never the credential. `has_credential` is the only thing said about the password. */
export interface DcfAccountRecord {
  state: DcfNativeState; email: string | null; unable_reason: string | null; has_credential: boolean; source: 'EMERGE' | 'MONDAY';
  recorded_at?: string | null; recorded_by_name?: string | null; credential_set_at?: string | null; version?: number;
  /** What the account is to this work; and, when this case has no record of its own, the resident's account from an earlier UMED. */
  need?: DcfNeed;
  existing?: { email: string | null; case_id: string } | null;
}
export interface ApprovalReview { id: string; reviewed_at: string; noca_result: NocaResult; flmmis_result: FlmmisResult; decision: ReviewDecision; note: string | null; reviewed_by_name: string | null }
export interface CorrectionReason { id: string; reason: string; recorded_at: string; recorded_by_name: string | null }
export interface Correction {
  id: string; correction_type: CorrectionType; state: 'REQUESTED' | 'SUBMITTED' | 'COMPLETED'; original_case_id: string | null;
  requested_at: string; submitted_at: string | null; completed_at: string | null; requested_by_name: string | null; reasons: CorrectionReason[];
  /** The note for Gwynn asked with it (2026-10-29), when one was written; null with `note_withheld` where it names NOCA or FLMMIS for this caller. */
  note?: string | null;
  note_withheld?: boolean;
}
/** addCorrectionNote's answer: `told` packet makers told now; `replayed` when the same note was already on it. */
export interface CorrectionNoteAdded { correctionId: string; noteId: string; noteResendAt: string | null; told: number; replayed: boolean }
/** DCF's answer on a resubmittal (POST /umed/reversals/:id/outcome). financeHandoff BLOCKED_EXISTING_HANDOFF: ruling B (2026-10-30). */
export interface ReversalOutcomeResult {
  state: ReversalState; completed: boolean; kind?: string | null; financeHandoffCreated?: boolean; financeHandoffAmended?: boolean;
  financeHandoffActionId?: string | null; financeHandoff?: 'BLOCKED_EXISTING_HANDOFF'; financeHandoffBlocked?: { code: string; message: string };
}
export interface CorrectionCompleted {
  completed: boolean; alreadyCompleted?: boolean; financeHandoffCreated?: boolean; financeHandoffAmended?: boolean; financeHandoffActionId?: string | null;
  // Rulings A/B (2026-10-30): OPENED (approved, no handoff: one opened), BLOCKED_EXISTING_HANDOFF (a PORTFOLIO claim beside the resident's live handoff).
  financeHandoff?: 'NOT_APPROVED' | 'OPENED' | 'BLOCKED_EXISTING_HANDOFF';
  financeHandoffBlocked?: { code: string; message: string };
}
export interface ReversalReason { id: string; reason: string; recorded_at: string; recorded_by_name: string | null }
/** What was true of the claim when the reversal was asked for — frozen, never recomputed. */
export interface ReversalPriorState {
  stage: Stage; noca_status: Noca; flmmis_status: Flmmis; approval_source: 'MONDAY' | 'EMERGE';
  finance_state: FinanceState; monday_finance_account_id: string | null; approved_on: string | null;
}
export interface Reversal {
  id: string; requester: ReversalRequester; state: ReversalState; outcome: ReversalOutcome | null; original_case_id: string | null;
  prior_state: ReversalPriorState; submission_count: number;
  requested_at: string; last_submitted_at: string | null; outcome_recorded_at: string | null; completed_at: string | null;
  requested_by_name: string | null; reasons: ReversalReason[];
  /** null = a legacy row whose kind was never stated. */
  kind?: ResubmittalKind | null;
  corrected_terms?: ResubmittalCorrectedTerms | null;
  handed_to_umed_at?: string | null; handed_by_name?: string | null;
  withdrawn_at?: string | null; withdrawn_reason?: string | null; withdrawn_by_name?: string | null;
}
/** The correction Finance states (CORRECTED only): whole cents. */
export interface ResubmittalCorrectedTerms { months: number; monthly_cents: number; total_cents: number; starts_on?: string | null; ends_on?: string | null; terms_text?: string | null }
/** POST /umed/cases/:id/reversals (2026-10-19): UMED starts a DCF resubmittal. `corrected_terms` for CORRECTED only. */
export interface StartResubmittalBody {
  requester: ReversalRequester; reason: string; kind: ResubmittalKind; corrected_terms?: ResubmittalCorrectedTerms; idempotency_key?: string;
}
/** `state` is where it stands now: WITH_UMED on a new start; on a replay, wherever it has got to since (2026-10-19). */
export interface StartResubmittalResult {
  reversalId: string; kind: ResubmittalKind; state: 'WITH_FINANCE' | 'WITH_UMED' | 'WITH_DCF' | 'DCF_DECLINED' | 'APPROVED' | 'WITHDRAWN' | null;
  startedIn: 'UMED'; replayed: boolean; statusWord?: string | null;
}

/** A sending of the packet, as recorded by whoever sent it. Append-only. Imported history says so (source_system MONDAY). */
export interface Submission {
  id: string; purpose: CreationReason; method: SubmissionMethod; reference: string | null; submitted_on: string; recorded_at: string; submitted_by_name: string | null;
  source_system?: 'EMERGE' | 'MONDAY'; packet_id?: string | null; packet_version?: number | null; historical_effective_at?: string | null;
  /** WRONG_DOCUMENT_SENT marks a corrected re-send (2026-10-14). */
  episode_cause?: string | null;
  /** The day as recorded on the sending, when a correction (2026-10-15) says another; `submitted_on` is the day in force. */
  recorded_submitted_on?: string | null;
  date_correction?: { from: string; to: string; reason: string | null; recorded_at: string | null; recorded_by_name: string | null } | null;
}

/** A document eMerge holds for the case's resident. Never a storage key; a URL is asked for one read at a time. */
export type DocumentClass = 'FACE_SHEET' | 'UMED' | 'RECORDING' | 'DCF' | 'AUDIOGRAM' | 'NOCA' | 'BOM_VETTING' | 'ACH' | 'UMED_GENERATED_PACKET' | 'FLMMIS';
export const DOCUMENT_CLASS_LABEL: Record<DocumentClass, string> = {
  FACE_SHEET: 'Face sheet', UMED: 'UMED', RECORDING: 'Recording', DCF: 'DCF', AUDIOGRAM: 'Audiogram', NOCA: 'NOCA', BOM_VETTING: 'BOM vetting', ACH: 'ACH', UMED_GENERATED_PACKET: 'Generated packet', FLMMIS: 'FLMMIS',
};
export interface CaseDocument {
  id: string; document_class: DocumentClass; original_filename: string | null; content_type: string; byte_size: number;
  source_system: 'MONDAY' | 'EMERGE' | 'DCF_MAIL'; source_created_at: string | null; imported_at: string | null; created_at: string; version: number; template_version?: string | null;
  /** False when the document hangs off another UMED case of the same resident (closed or sibling). Absent on older servers: treat as attached. */
  attached_to_this_case?: boolean;
  /** Where the row hangs (2026-10-14): this case, another UMED case of the resident, or the resident alone (a PA upload or an import). */
  filed_on?: 'THIS_CASE' | 'OTHER_CASE' | 'RESIDENT';
  uploaded_by_pa?: boolean;
  /** The packets that used or use this document: sent_on null means prepared, not yet sent. */
  packets?: Array<{ version: number; state: string; sent_on: string | null }>;
  /** "Not this resident's": the row is kept, struck through, with who said so and when. */
  misfiled?: { reason: string; at: string; by_name: string | null; outcome: string; sent: string | null } | null;
  refiled?: { at: string; by_name: string | null; mark_undone_at?: string | null } | null;
  /** Ruling R3: a "Not this resident's" mark on this row that a PA manager undid (in force again). */
  undone?: { at: string; reason: string; by_name: string | null } | null;
}
/** What a UMED operator may add (cutover 2026-09-21): the signed UMED, the audiogram, DCF's confirmation, the NOCA. */
export type UmedUploadClass = 'UMED' | 'AUDIOGRAM' | 'DCF' | 'NOCA' | 'FLMMIS';
export const UMED_UPLOAD_CLASSES: UmedUploadClass[] = ['DCF', 'NOCA', 'FLMMIS', 'UMED', 'AUDIOGRAM'];
export const UMED_UPLOAD_LABEL: Record<UmedUploadClass, string> = { UMED: 'Signed UMED', AUDIOGRAM: 'Audiogram', DCF: 'DCF confirmation', NOCA: 'NOCA', FLMMIS: 'FLMMIS evidence' };
/** An operator's note on the case (cutover 2026-09-21). Append-only. */
/**
 * `note` is null with `note_withheld` (2026-10-29) for a correction's note for Gwynn that names NOCA or FLMMIS, read by a
 * caller without approval detail -- the rule an action's reason follows.
 */
export interface UmedNote { id: string; note: string | null; recorded_at: string; recorded_by_name: string | null; note_withheld?: boolean }
/** What every screen says in place of a withheld note for Gwynn (the Kanban card, the case's corrections and notes). */
export const CORRECTION_NOTE_WITHHELD = 'A note for Gwynn is on the case. It is withheld here: it names NOCA or FLMMIS, which only approval reviewers may read.';

/** A generated packet version (umed-packet-v1). Immutable once rendered; state moves VALID → SUBMITTED | SUPERSEDED. */
export type PacketState = 'VALID' | 'SUBMITTED' | 'SUPERSEDED';
export type PacketReason = 'NEW_UMED' | 'CORRECTION' | 'REVERSAL' | 'RESUBMISSION';
export const PACKET_REASON_LABEL: Record<PacketReason, string> = { NEW_UMED: 'New UMED expense', CORRECTION: 'UMED correction', REVERSAL: 'Crossover UMED reversal', RESUBMISSION: 'UMED resubmission' };
export interface PacketValidation { ok: boolean; missing: string[]; warnings: { field: string; why: string }[] }
export interface Packet {
  id: string; version: number; template_version: string; purpose: CreationReason; packet_reason: PacketReason; document_id: string; sha256: string;
  validation: PacketValidation; state: PacketState; generated_at: string; generated_by_name: string | null;
  /** 2026-10-14 (PR #416 round 3): the classes in this revision marked "Not this resident's"; the screen says "Do not send". */
  carries_misfiled?: string[];
  fields: Record<string, string | number | null>; provenance: Record<string, string | null>;
  review_supported?: boolean; reviewed?: boolean;
}
export interface PacketInputs { client_case_number?: string; client_medicaid_pin?: string; pages?: string | number; comments?: string; payment_terms?: string; umed_change?: string; total?: string | number }
export interface PacketPreview {
  template_version: string; purpose: CreationReason; packet_reason: PacketReason; next_version: number;
  fields: Record<string, string | number | null>; ssn_on_file: boolean; provenance: Record<string, string | null>; validation: PacketValidation;
}
export const PACKET_FIELD_LABEL: Record<string, string> = {
  submission_reason: 'Submission reason', date_submitted: 'Date submitted', client_name: 'Client name', client_case_number: 'Client case #', client_ssn: 'Client SSN',
  client_dob: 'Client DOB', client_medicaid_pin: 'Client Medicaid PIN', sender: 'Sender', sender_phone: 'Sender phone', provider_attorney: 'Provider / attorney',
  pages: 'Pages (including cover)', comments: 'Comments', payment_terms: 'Payment terms', umed_change: 'UMED / change', total: 'Total', original_case_id: 'Original UMED',
};

/**
 * P0 2026-10-07: the packet eMerge prepares itself when a PA marks a case Ready
 * to submit (services/umed/packet/preparation.js). Preparation states, not case
 * stages.
 */
export type PacketPreparationState = 'PREPARING' | 'ACTION_REQUIRED' | 'FAILED' | 'READY' | 'WITHDRAWN' | 'SUBMITTED';
export type PacketBlockerOwner = 'PATIENT_ADVOCATE' | 'UMED_OPERATOR' | 'BUSINESS_RULING' | 'FINANCE';
export interface PacketBlocker { code: string; owner: PacketBlockerOwner; message: string; documentClass?: string | null; field?: string }
export const PACKET_BLOCKER_OWNER_LABEL: Record<PacketBlockerOwner, string> = {
  PATIENT_ADVOCATE: 'the Patient Advocate', UMED_OPERATOR: 'a UMED operator (on this case)', BUSINESS_RULING: 'a business ruling (Isaiah / April)',
  FINANCE: 'Finance',
};
/** What a board row carries (readModel PACKET_PREPARATION). */
export interface PacketPreparationRow {
  state: PacketPreparationState; blockers: PacketBlocker[]; error_code: string | null; withdrawn_reason: string | null;
  ready_at: string | null; version: number | null; pages: number | null; revision_kind: 'PREPARED' | 'FINAL' | null; packet_state: PacketState | null;
  review_required?: boolean; review_recorded?: boolean;
}
export type FaxResolution = 'NOT_SENT' | 'DELIVERED' | 'ACKNOWLEDGED';
export type FaxStatus = 'SENDING' | 'QUEUED' | 'IN_PROGRESS' | 'UNCERTAIN' | 'DELIVERED' | 'FAILED';
export interface FaxService { enabled: boolean; destination_display: string; can_send: boolean }
export interface FaxTransmission {
  id: string; packet_id: string; packet_version: number | null; sha256: string; attempt: number; status: FaxStatus; label: string;
  error_code: string | null; error_reason: string | null; pages: number | null; requested_at: string; completed_at: string | null;
  sending: 'PENDING' | 'RECORDED' | 'NEEDS_ATTENTION' | null; sending_reason: string | null; retry_allowed: boolean; in_flight: boolean;
  /** Flagged for a person (never failed by itself). All four are absent on an older server. */
  attention?: { code: 'UNCERTAIN_UNRESOLVED' | 'STALLED' | 'PROVIDER_NOT_FOUND' | 'NOT_RECORDED' | 'FOUND_AFTER_NOT_SENT' | 'PROVIDER_CONTRADICTS'; reason: string; since: string } | null;
  resolution?: { kind: 'MARKED_NOT_SENT' | 'MARKED_DELIVERED' | 'ACKNOWLEDGED'; reason: string; at: string } | null;
  /** RETRY_RECORDING (PR #416): "Try recording again", its own act (not a resolution), offered beside ACKNOWLEDGED. */
  resolve_options?: Array<FaxResolution | 'RETRY_RECORDING'>;
  /** Ruling R7 (2026-10-14): "Try recording again" is offered to the sender too (the resolutions are not). */
  can_retry_recording?: boolean;
  can_resolve?: boolean;
  /** This caller sent the fax: they may not resolve it (ruling, Isaiah Cotto 2026-10-12). */
  resolver_is_sender?: boolean;
  requested_by?: string;
  /** The fax service's id for this fax; null when eMerge never learned it. */
  provider_fax_id?: string | null;
  /** Verifier X1: the fax id was attached by a person from an unlabelled fax (an attested, never verified, delivery). */
  fax_id_attached_by_person?: boolean;
  /** Verifier X2: marked not sent after DCF confirmed it was not received. */
  dcf_confirmed_not_received?: boolean;
  /** Verifier L1: marked delivered on DCF's word that they received it. */
  dcf_confirmed_received?: boolean;
}
export interface PacketPreparationStatus {
  purpose: CreationReason; reason_box: string | null; reason_label: string | null;
  state: PacketPreparationState | null; label: string; blockers: PacketBlocker[]; last_error_code: string | null; attempts: number;
  requested_at: string | null; ready_at: string | null; withdrawn_reason: string | null; upload_ready: boolean;
  review?: { required: boolean; complete: boolean; reviewed_at: string | null };
  /**
   * Ruling R2 (2026-10-14): a corrected re-send is sent only after a person confirms its exact revision accurate.
   * `ready`: it can be confirmed now (reviewed and dated, not yet confirmed). Absent on an older server.
   */
  accuracy?: { required: boolean; statement: string | null; confirmed: { at: string; by_name: string | null } | null; ready: boolean };
  sending_ready?: boolean;
  /** Record fax sending (Isaiah Cotto, 2026-10-12): the exact reviewed revision may be recorded as faxed -- not held by a stuck benefits link. */
  fax_ready?: boolean;
  /** Gwynn, 2026-10-12: the DCF benefits link. STUCK holds Confirm DCF upload (the server refuses it, 409 DCF_BENEFITS_STUCK). */
  benefits?: DcfBenefits | null;
  /**
   * The DCF account as the sendings read it (2026-10-13): what it is to this work, this case's own record (null when it
   * has none), and why the upload / the fax is held by it -- the exact next act, in words -- or null when it is not.
   */
  dcf_account?: { need: DcfNeed; state: DcfNativeState | null; upload_hold: string | null; fax_hold: string | null };
  can_regenerate_correction?: boolean;
  /** Fax to DCF through the fax service (ships disabled). Absent on an older server: treat as disabled. */
  fax_service?: FaxService | null;
  /** The latest fax eMerge sent for this case. Absent on an older server: treat as none. */
  fax_transmission?: FaxTransmission | null;
  /** PR #420 round 8: every other fax of the case (any episode) DCF received that waits for a person. */
  faxes_waiting?: FaxTransmission[];
  live: { stale: boolean; withdrawn: string | null; submitted?: boolean; blockers: PacketBlocker[]; warnings: { field: string; why: string }[] };
  current: { packet_id: string; version: number; revision_kind: 'PREPARED' | 'FINAL'; generated_at: string; sha256: string; pages: number; document_id: string; state: PacketState; date_submitted: string | null;
    /** 2026-10-14: the classes in this revision marked "Not this resident's"; such a revision is never offered for sending. */
    carries_misfiled?: string[] } | null;
  identifiers: { dcf_case_number: { recorded: boolean; value?: string | null }; medicaid_pin: { recorded: boolean; value?: string | null } };
  /** Gwynn, 2026-10-12: the Comments printed on this episode's cover. `text` only to who may write it or open the packet. */
  cover_comment?: PacketCoverComment;
  submission: { id: string; submitted_on: string; recorded_at: string; method: string; channel: string | null; uploaded_on: string | null; reference: string | null } | null;
}
/** What the cover's Comments box holds: MAX_LINES lines of 10 pt Helvetica, at most MAX_CHARS characters (services/umed/packet/coverComments.js). */
export interface PacketCommentLimits { maxChars: number; maxLines: number; fontSize: number; leading: number; width: number }
export interface PacketCoverComment {
  recorded: boolean; id: string | null; text?: string; lines?: string[]; recorded_by_name?: string | null; recorded_at: string | null;
  /** The current revision prints exactly this comment (false while a new revision is being prepared). */
  printed_on_current: boolean; limits: PacketCommentLimits;
}
export interface PacketCommentPreview { text: string; lines: string[]; characters: number; limits: PacketCommentLimits }
export interface PacketCommentSaved { recorded: boolean; comment: { id: string; text: string; recorded_by: string; recorded_at: string } | null; lines: string[]; preparation: PreparationOutcome | null }
/**
 * What Confirm DCF upload / Record fax sending answer (preparation.sendExactPacket): the status word the sending wrote
 * (null when unchanged) and whether the approval reviewer's email was queued (nativeWorkflowService.recordSubmission).
 */
export interface PacketSendingRecorded {
  submissionId: string; channel?: string; verified?: boolean; packetRevision?: number; replayed?: boolean;
  statusWord?: string | null; approved?: boolean;
  submittedNotice?: { enqueued: boolean; reason: 'UNSET' | 'INVALID' | 'ALREADY_ENQUEUED' | 'NOT_A_SUBMITTED_SENDING' | null };
}
export interface PacketDownload { packetId: string; version: number; sha256: string; documentId: string; pages: number; dateSubmitted: string; reused: boolean; url: string; expires_in: number }
export interface PreparationOutcome { outcome: string; version?: number; pages?: number; code?: string; blockers?: PacketBlocker[] }

/** What Add the original terms answers: the row, and whether the packet is still held (and by what). */
export interface OriginalTermsResult {
  termsId: string; replayed: boolean; termsText: string; months: number; monthlyCents: number; totalCents: number; startsOn: string; endsOn: string;
  preparation: { outcome: string; blockers?: PacketBlocker[]; code?: string } | null;
}

/** One version of the terms a UMED operator settled. Append-only: a change is a new version. */
export interface TermsVersion {
  id: string; terms_text: string | null; months: number | null; monthly_cents: number | null; total_cents: number | null;
  starts_on: string | null; ends_on: string | null; note: string | null; recorded_at: string; recorded_by_name: string | null;
  /** Added as the original terms (2026-10-21), not a change. */
  original?: boolean;
}

export interface CaseDetail {
  case: UmedCase;
  actions: CaseAction[];
  events: CaseEvent[];
  facility: FacilityRef | null;
  advocate_case_id: string | null;
  dcf_account?: DcfAccountRecord;
  reviews?: ApprovalReview[];
  corrections?: Correction[];
  reversals?: Reversal[];
  submissions?: Submission[];
  documents: { items: CaseDocument[]; available: boolean; why: string | null; corrections?: Array<{ id: string; documentId: string; reason: string; at: string; by_name: string | null; outcome: string }> };
  /** Document correction (2026-10-14): offered when a sent packet carried a document now marked "Not this resident's". */
  corrected_resend?: {
    offered: boolean;
    open: { eventId: string; openedAt: string } | null;
    replaces: { packetVersion: number; sentOn: string; methods: string[]; words: string; wrongDocuments: string[] } | null;
    suggestedComment: string | null;
  } | null;
  /** The operators' notes, newest first (cutover 2026-09-21). Absent on a payload from before the deploy. */
  notes?: UmedNote[];
  /** Every version of the settled terms, newest first, with who and when (2026-10-06). */
  /** `board_text`: the board's own terms sentence, a reference only (2026-10-21) -- never read into a figure. */
  terms?: { current: TermsVersion | null; history: TermsVersion[]; board_text?: string | null;
    /** Add the original terms is offered (and accepted) only when the packet would otherwise lack its terms or total. */
    original_needed?: boolean };
  packets?: Packet[];
  submission: {
    state: 'SUBMITTED' | 'NOT_SUBMITTED' | 'UNKNOWN'; submitted_on: string | null; sent_to_dcf_on: string | null; evidence_state: string;
    purpose?: CreationReason; method?: SubmissionMethod; reference?: string | null; packet_version?: number | null;
  };
  /** The generation path is native (D-XF-36). The board's template is retired with the board. */
  generator?: { kind: 'NATIVE' | 'MONDAY_TEMPLATE_INTERIM' | 'NONE'; template_version?: string; url: string | null; what: string };
  provenance?: {
    source_system: string; source_record_type: string; source_record_id: string; source_rule: string;
    source_terms_raw: string | null; pa_lead_id: string | null; projected_at: string; vocabulary_version: number;
  };
}

export interface Counts {
  my_work: number;
  my_open_cases: number;
  lenses: Partial<Record<LensKey, number>>;
  /**
   * The same predicates over everything this operator may see, beside what
   * they hold (packet §14). Two numbers because one cannot say which it is,
   * and on 2026-09-21 three operators read one chip three different ways.
   */
  lenses_all: Partial<Record<LensKey, number>>;
  /** EP-010: each board view's badge, derived from `lenses_all` so a tab's badge and its rows can never disagree. */
  board_tabs?: Partial<Record<BoardTabKey, number>>;
  follow_ups: number;
  follow_ups_overdue: number;
  follow_ups_all: number;
  follow_ups_all_overdue: number;
  approved: number;
  approved_finance_missing: number;
  all_cases: number;
  exceptions: Partial<Record<ExceptionKey, number>>;
  unowned: number;
  facility_unresolved: number;
  /** When eMerge's half-hourly update last ran: the header's "Updated …". */
  last_projection_at: string | null;
  /** Kept for readers: the Monday sync time the last run read, and the Monday tables' latest sync. */
  board_read_at: string | null;
  source_synced_at: string | null;
  /** 2026-10-12: Monday's sync is retired for good; its last write is said neutrally, never as "not current". */
  monday_retired?: boolean;
  monday_final_sync_at?: string | null;
  /** eMerge's own update (the last projection run): the only thing this is measured from. */
  freshness: Freshness;
}

export interface LensDef { key: LensKey; label: string; primary: boolean; what: string }
export interface ExceptionDef { key: ExceptionKey; label: string; what: string }

/* ── EP-010 §9–§12: the board-parity table ─────────────────────────────── */

/**
 * The ten operating views Josie reconciles across, exactly as the live board
 * carries them. `board` names the view each replaces, so a difference found
 * while reconciling can be traced to a rule instead of argued about; `lens` is
 * the native predicate it reads through, because a tab here is a filter over
 * one case record and never a group a row was dragged into.
 */
export type BoardTabKey =
  | 'umeds-needed'
  // 2026-09-24: the board's own READY TO SUBMIT group, beside eMerge's derived
  // one. April's list held 37 and the derived view 302, overlapping by 9; both
  // are legitimate and they answer different questions, so both are offered.
  | 'board-umeds-needed'
  | 'review-for-approvals' | 'main-table' | 'umeds-to-fax' | 'umed-submitted'
  | 'dcf-submitted-flmmis' | 'dcf-corrections' | 'reversals' | 'dcf-login-needed' | 'new-approvals-noca'
  | 'ready-to-approve' | 'not-qualifying'
  // Verification of #378 (4): the board's moves into Finance or closed that eMerge did not apply.
  | 'monday-not-applied'
  // UM-R1 (2026-10-02): not one of the board's ten views -- the home PROBLEM ACCOUNT needed once it left Review for Approvals.
  | 'problem-accounts'
  // Josie Evans, second letter (2026-10-06): SFS CORRECTION COMPLETED's own category, and the cases sent in Monday that eMerge holds at Ready.
  | 'dcf-correction-completed' | 'sent-in-monday'
  // Gwynn, 2026-10-12: the board's DCF EMAIL CREATED = Stuck, natively, with her reason.
  | 'dcf-benefits-stuck'
  // Processes 5 and 6 (2026-10-12): an open PL correction or benefits-lost re-vet; and the re-vets due within 7 days.
  | 'after-approval' | 'revet-due'
  // APR 02 (2026-10-13): FLMMIS checks due.
  | 'flmmis-check-due';
/**
 * A section of a tab (Review for Approvals: DCF RESUB NEEDED, then UMED SUBMITTED TO DCF; DCF Corrections, 2026-10-28:
 * corrections to send, then corrections submitted and awaiting review).
 */
export type ReviewSectionKey = 'dcf-resub-needed' | 'umed-submitted-to-dcf' | 'correction-to-send' | 'correction-submitted';
export interface BoardSectionDef { key: ReviewSectionKey; word: string; label: string; what: string }
export interface BoardTabDef { key: BoardTabKey; label: string; lens: string; board: string; what: string; sections?: BoardSectionDef[];
  /** What one row is: RESIDENT on a work tab (the newest case meeting its predicate), CASE on Main table. The badge counts the same. */
  rows?: 'RESIDENT' | 'CASE' }

/**
 * One status word's place in the learned colour language. `label` is always
 * rendered; colour is supplemental and never the only signal (§12, §17).
 * `caveat` is present where eMerge deliberately refuses the board's reading —
 * APPROVED is a source word, not an approval.
 */
export interface LegendEntry { word: string; label: string; color: string; caveat?: string }

/**
 * Isaiah Cotto, 2026-10-14: the table defaults to Most recent ('recent': the case created last first), with Longest
 * waiting ('waiting': sent to DCF, else submitted, oldest first) a named choice beside the column sorts. Each named
 * order has one direction. 'lead-closed' (second ruling of the same day) sorts on the day the Patient Advocate closed
 * the sale behind the UMED (pa_case_sales), no longer on the UMED case's own closed_on.
 */
export type BoardSort = 'recent' | 'waiting'
  | 'resident' | 'facility' | 'status' | 'submitted' | 'sent-to-dcf' | 'approved' | 'last-follow-up' | 'liability' | 'sale' | 'available'
  | 'lead-closed';

/** A board row: the case, plus the reconciliation columns §11 asks for. */
import type { FinancialResponsibility } from '../advocate/advocateApi';
export type { FinancialResponsibility };

/** "Paid in full, UMED pending" as a row carries it (backend umed/paidInFull.paidInFullMarkOf). */
export interface PaidInFullMark { state: 'MARKED' | 'CLEARED'; reason: string | null; paid_on: string | null; via: 'UMED' | 'FINANCE' | null; recorded_at: string | null; recorded_by_name: string | null }
export interface PaidInFullMarkResult { state: 'MARKED'; reason: string; paidOn: string | null; warnings: string[]; replayed: boolean }
export interface SendingDateCorrection { submissionId: string; from: string; to: string; caseDaysMoved: boolean; checkMoved: { actionId: string; dueOn: string } | null; replayed: boolean }
/** Said when a mark is recorded while a reversal or correction is open (ruling 2026-10-15: never refused for it). */
export const RESUBMITTAL_OPEN_WORDS = 'A correction/resubmittal is open: its terms may need updating for the payment.';

export interface BoardRow extends UmedCase {
  /** "Date lead closed" (2026-10-14): the New York day the Patient Advocate closed the sale behind this UMED; null when no sale is on record. Absent from an older server. */
  lead_closed_on?: string | null;
  /** EXACT (eMerge saw the close) or SOURCE_REPORTED (Monday's Date Closed). */
  lead_closed_confidence?: 'EXACT' | 'SOURCE_REPORTED' | null;
  resident_dob: string | null;
  case_number: string | null;
  /** Who manages the resident's funds. MONDAY-owned, read-only, often absent. */
  financial_responsibility: FinancialResponsibility | null;
  /** Whether there is a number to reveal. Never the number itself. */
  ssn_on_file: boolean;
  /**
   * When the lead became available. The TWL Date when the source recorded one
   * (`TWL_DATE`), otherwise when eMerge first saw the case (`FIRST_SEEN`) —
   * and the row says which, because "available 3 days ago" and "we noticed it
   * 3 days ago" are different statements about different things.
   */
  lead_available: { on: string; basis: 'TWL_DATE' | 'FIRST_SEEN' } | null;
  lead_assigned_on: string | null;
  advocate_name: string | null;
  /** Cents. `null` means no native fact — not zero, and never the Monday mirror. */
  patient_liability_cents: number | null;
  patient_liability_status: string | null;
  /** Dollars, from the linked PA sale. */
  sale_amount: number | null;
  sale_amount_source: 'MONDAY_CLOSED_LEAD_TOTAL' | 'NATIVE_PRICING_CATALOG' | 'APPROVED_OVERRIDE' | null;
  sale_product_label: string | null;
  /**
   * What the case is being paid on. `source: 'EMERGE'` means a UMED operator
   * settled them with DCF; `'SALE'` means it is still the advocate's snapshot
   * from when the lead closed.
   */
  submitted_terms: {
    // Null when a typed line did not read as months and a payment (2026-10-02); `text` then carries it.
    status: string; months: number | null; monthly_cents: number | null;
    starts_on?: string | null; ends_on?: string | null; source?: 'EMERGE' | 'SALE';
    /** The terms line exactly as the UMED operator typed it, when she typed one. */
    text?: string | null;
  } | null;
  enrollment_state: 'NOT_ENROLLED' | 'PENDING_CONFIRMATION' | 'ENROLLED' | 'NOT_ENROLLED_FINAL';
  enrollment_confirmed_at: string | null;
  /** The legend entry for this row's SOURCE word, or null when the legend does not know it. */
  status_legend: LegendEntry | null;
  /**
   * UM-R2: EMERGE when a native act here set the word, MONDAY when it is the board's (a case the board still
   * forwards). ADOPTED (authority ruling 2026-10-05): the word a board-origin case carried into adoption --
   * eMerge's from then on, moved only by acts recorded here.
   */
  status_word_source?: 'EMERGE' | 'MONDAY' | 'ADOPTED' | null;
  /**
   * An everywhere search only (2026-10-06): the board views whose predicate this row meets, in tab order.
   * Empty for a closed case and for one only Main table holds. Absent on a tab's own rows.
   */
  board_views?: BoardTabKey[];
  /** The section this row sits in (Review for Approvals, DCF Corrections); null on every other tab (2026-10-06). */
  review_section?: ReviewSectionKey | null;
  /** The version the row was read at; a move made from the row sends it back (409 STALE). */
  updated_at?: string | null;
}

export interface BoardPage extends Paged<BoardRow> {
  tab: BoardTabKey;
  board_view: string;
  lens: string;
  what: string;
  /** The order the rows are in: what was asked, else the default ('recent'); null only for a search across every case with no sort asked (open first, by name). */
  sort: BoardSort | null;
  dir: 'asc' | 'desc';
  /** The named orders first ('recent', 'waiting'), then the column sorts. */
  sorts: BoardSort[];
  /** The table's default order (2026-10-14: 'recent'). Absent from an older server. */
  default_sort?: BoardSort;
  /** EVERYWHERE: the rows answer a search across every case in scope, not the tab (2026-10-06). */
  scope?: 'TAB' | 'EVERYWHERE';
  /** The tab's sections with their counts, from the same rows (they sum to `total`). Null on a tab without sections. */
  sections?: Array<BoardSectionDef & { count: number }> | null;
  /**
   * A search IN A TAB (round 4, 2026-10-07): how many cases the same search finds across every case in scope -- the
   * rows "Search every case" opens. Null when not searching a tab.
   */
  everywhere_total?: number | null;
}

export const ENROLLMENT_LABEL: Record<BoardRow['enrollment_state'], string> = {
  NOT_ENROLLED: 'Not enrolled', PENDING_CONFIRMATION: 'Pending confirmation', ENROLLED: 'Enrolled', NOT_ENROLLED_FINAL: 'Did not enrol',
};

export interface Scope {
  /** Replacement requests waiting on the Replacements desk (absent on an older server, null when it could not be counted: no badge). */
  replacements?: { open: number } | null;
  operator: boolean;
  approval_review: boolean;
  finance_visible: boolean;
  provenance_visible: boolean;
  enterprise: boolean;
  facility_count: number | null;
  /** Processes 5 and 6: the server's windows and outcome words. */
  after_approval?: { revet_days: number; revet_visible_days_before: number; pl_outcomes: Array<'CORRECTED' | 'DNQ'>; revet_outcomes: Array<'REQUALIFIED' | 'DNQ'> };
  counts: Counts;
  lenses: LensDef[];
  /** EP-010: the ten board views and the colour legend, from the server. The screen invents neither. */
  board_tabs?: BoardTabDef[];
  status_legend?: LegendEntry[];
  exception_kinds: ExceptionDef[];
  vocabulary_version: number;
  recheck_days: number;
  correction_types?: CorrectionType[];
  reversal_requesters?: ReversalRequester[];
  submission_methods?: SubmissionMethod[];
  /** The dispositions the server accepts (2026-10-02). */
  dispositions?: Disposition[];
  documents_visible?: boolean;
  packet_template?: string;
  /** NATIVE only on a process where the structural gate is open. */
  writes: 'NONE' | 'NATIVE';
  /** What this caller may do, when writes are NATIVE. The server decides again on every call. */
  can?: { review_approval: boolean; manage_dcf_account: boolean; reveal_dcf_credential: boolean; manage_corrections: boolean; manage_reversals?: boolean; prepare_submission?: boolean; generate_packet?: boolean; claim_case?: boolean; add_document?: boolean; add_note?: boolean; adopt_case?: boolean; confirm_enrollment?: boolean; record_disposition?: boolean; lift_blocker?: boolean; settle_exception?: boolean; set_stage?: boolean;
    /** Processes 5 and 6: record a PL correction / Medicaid benefits lost, and answer a PL correction. */
    record_after_approval?: boolean;
    /** Signed send-back to the originating PA (docs/record-flow/send-back-design.md §4.1): held AND its own gate open AND the key configured. */
    send_back?: boolean };
}
/** A fresh key per deliberate click, so a retry of the same click is a replay and a second click is a new act. */
export const freshKey = (): string => (typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);
export const canDo = (s: Scope, what: keyof NonNullable<Scope['can']>): boolean => s.writes === 'NATIVE' && Boolean(s.can && s.can[what]);

export interface ApiResult<T> { success: boolean; data?: T; error?: string; code?: string; message?: string; status?: number; detail?: any }
export interface ListQuery { q?: string; page?: number; lens?: LensKey | null; kind?: ExceptionKey | null; whose?: 'mine' | 'all' }

function qs(params: Record<string, string | number | null | undefined>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
}

/**
 * `api.get` is `apiRequest.bind(...)` and loses its generic, so each call
 * asserts its own result type — the same fix already applied in emergeApi.ts.
 */
export const umedApi = {
  /** The list the screen is showing, as a CSV. Null on success, else a message to show. */
  exportWork: async (l: { q?: string; lens?: string | null }): Promise<string | null> => apiDownloadFile('/umed/work/export', { q: l.q, lens: l.lens }),
  exportAll: async (l: { q?: string; lens?: string | null }): Promise<string | null> => apiDownloadFile('/umed/all/export', { q: l.q, lens: l.lens }),
  scope: async (): Promise<ApiResult<Scope>> => (await api.get('/umed')) as ApiResult<Scope>,
  work: async (l: ListQuery): Promise<ApiResult<WorkPage>> => (await api.get(`/umed/work${qs({ lens: l.lens, q: l.q, page: l.page })}`)) as ApiResult<WorkPage>,
  followUps: async (l: ListQuery): Promise<ApiResult<FollowUpPage>> => (await api.get(`/umed/follow-ups${qs({ whose: l.whose, q: l.q, page: l.page })}`)) as ApiResult<FollowUpPage>,
  approved: async (l: ListQuery): Promise<ApiResult<ApprovedPage>> => (await api.get(`/umed/approved${qs({ q: l.q, page: l.page })}`)) as ApiResult<ApprovedPage>,
  exceptions: async (l: ListQuery): Promise<ApiResult<ExceptionPage>> => (await api.get(`/umed/exceptions${qs({ kind: l.kind, q: l.q, page: l.page })}`)) as ApiResult<ExceptionPage>,
  all: async (l: ListQuery): Promise<ApiResult<Paged<UmedCase>>> => (await api.get(`/umed/all${qs({ lens: l.lens, q: l.q, page: l.page })}`)) as ApiResult<Paged<UmedCase>>,
  case: async (id: string): Promise<ApiResult<CaseDetail>> => (await api.get(`/umed/cases/${id}`)) as ApiResult<CaseDetail>,
  /** EP-010 §9-§11: one board view, as a table. An unknown tab is refused by the server, never widened. */
  board: async (l: { tab: BoardTabKey; q?: string; page?: number; sort?: BoardSort | null; dir?: 'asc' | 'desc' | null; everywhere?: boolean }, signal?: AbortSignal): Promise<ApiResult<BoardPage>> =>
    // `signal` (round 4): search as you type cancels the request a newer keystroke made stale.
    (await (signal
      ? api.get(`/umed/board${qs({ tab: l.tab, q: l.q, page: l.page, sort: l.sort, dir: l.dir, everywhere: l.everywhere ? 1 : null })}`, undefined, { signal })
      : api.get(`/umed/board${qs({ tab: l.tab, q: l.q, page: l.page, sort: l.sort, dir: l.dir, everywhere: l.everywhere ? 1 : null })}`))) as ApiResult<BoardPage>,
  /** EP-010 §8: the VA confirms the resident enrolled. A state on the existing sale, never a second sale. */
  confirmEnrollment: async (id: string, body: { state?: 'ENROLLED' | 'PENDING_CONFIRMATION' | 'NOT_ENROLLED_FINAL'; reason?: string | null; idempotency_key: string }):
    Promise<ApiResult<{ saleId: string | null; state: string; changed: boolean; replayed: boolean; paOutcome: string }>> =>
    (await api.post(`/umed/cases/${id}/enrollment`, body)) as ApiResult<{ saleId: string | null; state: string; changed: boolean; replayed: boolean; paOutcome: string }>,

  /* ── Phase 3 native writes. Each 404s on a process where the gate is closed. ── */
  /**
   * `follow_up_on` is the day the reviewer looked, when that was not today
   * (Josie Evans, 2026-09-24). Absent means today, which is what every
   * existing caller means. The server refuses a future date and a date more
   * than a year back; it does not otherwise argue with the reviewer.
   */
  review: async (id: string, body: { noca: NocaResult; flmmis: FlmmisResult; decision: 'CONTINUE' | 'RESUBMIT'; note?: string; follow_up_on?: string }): Promise<ApiResult<{ reviewId: string; decision: ReviewDecision; fullyApproved: boolean; readyToApprove?: boolean }>> =>
    (await api.post(`/umed/cases/${id}/reviews`, body)) as ApiResult<{ reviewId: string; decision: ReviewDecision; fullyApproved: boolean; readyToApprove?: boolean }>,
  /**
   * Move the case to Approved (2026-10-06): the operator's explicit act, refused without FLMMIS reflected
   * on record. It alone opens the Finance handoff. A replay answers `replayed: true` and changes nothing.
   */
  approve: async (id: string, body: { approved_on?: string } = {}): Promise<ApiResult<{ caseId: string; approved: boolean; replayed: boolean; approvedOn: string | null; financeHandoffCreated: boolean; financeHandoffAdopted?: boolean; financeHandoff?: string }>> =>
    (await api.post(`/umed/cases/${id}/approve`, body)) as ApiResult<{ caseId: string; approved: boolean; replayed: boolean; approvedOn: string | null; financeHandoffCreated: boolean; financeHandoffAdopted?: boolean; financeHandoff?: string }>,
  /**
   * The NOCA arrived after the approval (2026-10-06): NOCA received with its date, nothing else. Approved cases only;
   * a NOCA already received answers `replayed: true`.
   */
  recordLateNoca: async (id: string, body: { received_on?: string } = {}): Promise<ApiResult<{ caseId: string; noca: 'RECEIVED'; replayed: boolean; receivedOn: string | null }>> =>
    (await api.post(`/umed/cases/${id}/noca-received`, body)) as ApiResult<{ caseId: string; noca: 'RECEIVED'; replayed: boolean; receivedOn: string | null }>,
  /**
   * Monday says this went to DCF and eMerge does not: the operator records the sending (2026-10-06). Never automatic.
   * Monday's word is read by the server, never sent from here (verification P2-B).
   */
  markSubmitted: async (id: string, body: { submitted_on?: string } = {}): Promise<ApiResult<{ caseId: string; stageAdvanced: boolean; replayed: boolean; submittedOn: string }>> =>
    (await api.post(`/umed/cases/${id}/mark-submitted`, body)) as ApiResult<{ caseId: string; stageAdvanced: boolean; replayed: boolean; submittedOn: string }>,
  /**
   * DCF RESUB NEEDED (Josie Evans, 2026-10-06): one click, an optional note for Gwynn, who is told in-app. The case
   * moves to the DCF Resub section and stays in Review for Approvals. `expected_updated_at` is the row's version (409 STALE).
   */
  dcfResubmission: async (id: string, body: { note?: string | null; expected_updated_at?: string | null; idempotency_key: string }):
    Promise<ApiResult<{ caseId: string; eventId: string; statusWord: string | null; replayed: boolean; notified?: { recipients: number } }>> =>
    (await api.post(`/umed/cases/${id}/dcf-resubmission`, body)) as ApiResult<{ caseId: string; eventId: string; statusWord: string | null; replayed: boolean; notified?: { recipients: number } }>,
  /**
   * CounselEAR's "Employee ID", typed once. CounselEAR will not send it --
   * PatientByID returns 23 fields and none of them is this one -- so eMerge
   * is where it starts existing.
   */
  /**
   * The terms as DCF settled them. Isaiah Cotto, 2026-09-28: "Anyone with
   * access to the UMED should be able to change terms."
   */
  recordTerms: async (id: string, body: {
    // The typed line (2026-10-02) is the record; the numbers are filled when it reads, or sent from the Details fold.
    terms_text?: string | null;
    /** 'details' when the numbers come from the Details fold: only then does the server take them over a line it cannot read. */
    entered?: 'details' | null;
    months?: number | null; monthly_cents?: number | null; total_cents?: number | null;
    starts_on?: string | null; ends_on?: string | null; note?: string | null; idempotency_key: string;
  }): Promise<ApiResult<{ termsId: string; months: number | null; replayed: boolean; derived?: 'SALE_TOTAL' | null; termsText?: string | null }>> =>
    (await api.post(`/umed/cases/${id}/terms`, body)) as ApiResult<{ termsId: string; months: number | null; replayed: boolean; derived?: 'SALE_TOTAL' | null; termsText?: string | null }>,

  /**
   * Add the original terms (2026-10-21): only on a case with no terms in eMerge. Typed, never read from the board;
   * checked on the server (months x monthly = total, the month span = the months). The packet is prepared again.
   */
  recordOriginalTerms: async (id: string, body: {
    months: number; monthly_cents: number; total_cents: number; starts_on: string; ends_on: string; note: string; idempotency_key: string;
  }): Promise<ApiResult<OriginalTermsResult>> =>
    (await api.post(`/umed/cases/${id}/original-terms`, body)) as ApiResult<OriginalTermsResult>,

  /**
   * Who manages the resident's funds, from the UMED board.
   *
   * Josie Evans, 2026-09-29: "There is no where for me to change the status."
   * Writes the same resident-scoped row the PA side writes -- two surfaces,
   * one truth.
   */
  /**
   * The day she last checked, asserting NOTHING about NOCA or FLMMIS.
   *
   * Josie Evans, 2026-09-30: "I went to update that I checked her this
   * morning on 9/30/26. Her name just disappeared." Recording the date
   * through `review` erased the case's FLMMIS state, because a review cannot
   * say "unchanged" and the caller coerced the unsayable into NOT_RECORDED.
   */
  /**
   * A disposition, or REACTIVATE (2026-10-02). The reason is required by the
   * server for every one; `effective_on` is the day it took effect (absent =
   * today). One key per deliberate click, so a retry is a replay.
   */
  recordDisposition: async (id: string, body: { disposition: Disposition; reason: string; effective_on?: string; idempotency_key: string }):
    Promise<ApiResult<{ caseId: string; disposition: Disposition; eventId: string; replayed: boolean }>> =>
    (await api.post(`/umed/cases/${id}/disposition`, body)) as ApiResult<{ caseId: string; disposition: Disposition; eventId: string; replayed: boolean }>,

  recordFollowUp: async (id: string, followUpOn: string): Promise<ApiResult<{ caseId: string; on: string }>> =>
    (await api.post(`/umed/cases/${id}/follow-up`, { follow_up_on: followUpOn })) as ApiResult<{ caseId: string; on: string }>,

  recordFinancialResponsibility: async (id: string, body: { type: string; ach_on_file?: boolean; note?: string | null; idempotency_key?: string }): Promise<ApiResult<{ id: string; type: string; achOnFile: boolean; replayed: boolean }>> =>
    (await api.post(`/umed/cases/${id}/financial-responsibility`, body)) as ApiResult<{ id: string; type: string; achOnFile: boolean; replayed: boolean }>,

  recordMedicaidNumber: async (id: string, medicaidNumber: string): Promise<ApiResult<{ changed: boolean; replayed: boolean }>> =>
    (await api.post(`/umed/cases/${id}/medicaid-number`, { medicaid_number: medicaidNumber })) as ApiResult<{ changed: boolean; replayed: boolean }>,
  assignDcfEmail: async (id: string, email: string): Promise<ApiResult<{ state: DcfNativeState; email: string }>> =>
    (await api.post(`/umed/cases/${id}/dcf-account/email`, { email })) as ApiResult<{ state: DcfNativeState; email: string }>,
  /** `password` omitted = keep the one on record (a generated login, UM-R4). */
  dcfAccountCreated: async (id: string, body: { email?: string; password?: string }): Promise<ApiResult<{ state: DcfNativeState; email: string }>> =>
    (await api.post(`/umed/cases/${id}/dcf-account/created`, body)) as ApiResult<{ state: DcfNativeState; email: string }>,
  dcfAccountUnable: async (id: string, reason: string): Promise<ApiResult<{ state: DcfNativeState; reason: string }>> =>
    (await api.post(`/umed/cases/${id}/dcf-account/unable`, { reason })) as ApiResult<{ state: DcfNativeState; reason: string }>,
  /**
   * Gwynn, 2026-10-12: "(STUCK) Can't link benefits", with the short reason (3 to 500 characters). STUCK = account made
   * (Isaiah Cotto, 2026-10-13): the act also records the case's DCF account as created when it is not yet; `email` and
   * `password` only where the case has none on record (the server answers DCF_EMAIL_REQUIRED / DCF_PASSWORD_REQUIRED).
   */
  dcfBenefitsStuck: async (id: string, body: { reason: string; email?: string; password?: string; idempotency_key?: string }): Promise<ApiResult<BenefitsStuckResult>> =>
    (await api.post(`/umed/cases/${id}/dcf-account/benefits-stuck`, body)) as ApiResult<BenefitsStuckResult>,
  /** The benefits are linked: the stuck mark is cleared, with an optional note. */
  dcfBenefitsCleared: async (id: string, body: { note?: string; idempotency_key?: string }): Promise<ApiResult<{ state: 'CLEARED'; replayed: boolean }>> =>
    (await api.post(`/umed/cases/${id}/dcf-account/benefits-cleared`, body)) as ApiResult<{ state: 'CLEARED'; replayed: boolean }>,
  /** "Paid in full, UMED pending" (2026-10-15): marked by the approval reviewer, with a reason; paid_on optional. */
  markPaidInFull: async (id: string, body: { reason: string; paid_on?: string | null; idempotency_key?: string }): Promise<ApiResult<PaidInFullMarkResult>> =>
    (await api.post(`/umed/cases/${id}/paid-in-full-mark`, body)) as ApiResult<PaidInFullMarkResult>,
  /** Clearing a mistaken mark, with a reason. */
  clearPaidInFull: async (id: string, body: { reason: string; idempotency_key?: string }): Promise<ApiResult<{ state: 'CLEARED'; replayed: boolean }>> =>
    (await api.post(`/umed/cases/${id}/paid-in-full-mark/clear`, body)) as ApiResult<{ state: 'CLEARED'; replayed: boolean }>,
  /** The day a sending went to DCF, corrected with a reason (2026-10-15). The sending row itself is never changed. */
  correctSendingDate: async (caseId: string, submissionId: string, body: { corrected_on: string; reason: string; idempotency_key?: string }): Promise<ApiResult<SendingDateCorrection>> =>
    (await api.post(`/umed/cases/${caseId}/submissions/${submissionId}/date`, body)) as ApiResult<SendingDateCorrection>,
  /** The one read of the password. Audited server-side; held in component state only, never stored. */
  revealDcfCredential: async (id: string, reason: string): Promise<ApiResult<{ email: string; password: string; revealEventId: string }>> =>
    (await api.post(`/umed/cases/${id}/dcf-account/reveal`, { reason })) as ApiResult<{ email: string; password: string; revealEventId: string }>,
  /**
   * `note_to_gwynn` is REQUIRED for PAID_IN_FULL_CORRECTION from UMED (2026-10-29: 400 NOTE_REQUIRED). `told`: how many
   * packet makers were told now (absent on a replay).
   */
  requestCorrection: async (id: string, body: { correction_type: CorrectionType; reason: string; note_to_gwynn?: string; months?: string[]; idempotency_key?: string }): Promise<ApiResult<{ correctionId: string; replayed?: boolean; told?: number }>> =>
    (await api.post(`/umed/cases/${id}/corrections`, body)) as ApiResult<{ correctionId: string; replayed?: boolean; told?: number }>,
  addCorrectionReason: async (correctionId: string, reason: string): Promise<ApiResult<{ reasonId: string }>> =>
    (await api.post(`/umed/corrections/${correctionId}/reasons`, { reason })) as ApiResult<{ reasonId: string }>,
  /**
   * APPROVED - SFS CORRECTION (PAR 01; Isaiah Cotto's ruling of 2026-10-13): the approval act for the months FLMMIS
   * reflects, and an SFS correction for `missing_months` ('YYYY-MM', at least one) in the same transaction. One Finance
   * handoff, as any approval. A replay answers `replayed: true`.
   */
  approvePartially: async (id: string, body: { missing_months: string[]; approved_months?: string[]; note?: string; approved_on?: string }): Promise<ApiResult<{ caseId: string; approved: boolean; partial: boolean; replayed: boolean; correctionId: string | null; missingMonths: string[] }>> =>
    (await api.post(`/umed/cases/${id}/approve-partial`, body)) as ApiResult<{ caseId: string; approved: boolean; partial: boolean; replayed: boolean; correctionId: string | null; missingMonths: string[] }>,
  /** PAR 03: FLMMIS now shows the corrected months -- the correction completes on the same handoff and the status is APPROVED. */
  completePartialApproval: async (id: string, body: { reflected_on?: string; note?: string } = {}): Promise<ApiResult<{ caseId: string; completed: boolean; replayed: boolean }>> =>
    (await api.post(`/umed/cases/${id}/approve-partial/complete`, body)) as ApiResult<{ caseId: string; completed: boolean; replayed: boolean }>,
  /** What completing reached on Finance's side (completeCorrection): a new handoff, an amendment, or nothing (NOT_APPROVED). */
  /** `sentOutsideEmerge` (2026-10-29): the attestation an unsent correction needs; the server refuses 409 CORRECTION_NOT_SENT without it. */
  /** "Add a note for Gwynn" on an open correction (2026-10-29): its one note; refused NOTE_ALREADY_ON_CORRECTION when it has one. */
  addCorrectionNote: async (correctionId: string, note: string): Promise<ApiResult<CorrectionNoteAdded>> =>
    (await api.post(`/umed/corrections/${correctionId}/note`, { note })) as ApiResult<CorrectionNoteAdded>,
  // `evidence` (2026-10-29, the row's "Already sent to DCF outside eMerge"): the day and how, kept in completion_evidence.
  completeCorrection: async (correctionId: string, opts: { sentOutsideEmerge?: boolean; evidence?: { sentOutsideOn: string; note: string } } = {}): Promise<ApiResult<CorrectionCompleted>> =>
    (await api.post(`/umed/corrections/${correctionId}/complete`, {
      ...(opts.sentOutsideEmerge ? { sent_outside_emerge: true } : {}),
      ...(opts.evidence ? { evidence: opts.evidence } : {}),
    })) as ApiResult<CorrectionCompleted>,
  /**
   * "Still not reflecting -- send the correction again" (Josie Evans, 2026-10-28): the open correction went to DCF and
   * DCF still does not show the months. Re-opens it for a new corrected packet; Gwynn is told. Idempotent on the key.
   */
  resendCorrection: async (correctionId: string, body: { months: string[]; reason: string; idempotency_key?: string }): Promise<ApiResult<{ correctionId: string; resend: number; state: string; statusWord: string | null; replayed: boolean }>> =>
    (await api.post(`/umed/corrections/${correctionId}/send-again`, body)) as ApiResult<{ correctionId: string; resend: number; state: string; statusWord: string | null; replayed: boolean }>,
  /*
   * U-18: the DCF resubmittal, natively. Since 2026-10-19 UMED starts one too (umed.reversal.manage): the same start as
   * Finance's, handed to UMED in the same act. Idempotent on the key the form keeps for as long as it is open.
   */
  startResubmittal: async (caseId: string, body: StartResubmittalBody): Promise<ApiResult<StartResubmittalResult>> =>
    (await api.post(`/umed/cases/${caseId}/reversals`, body)) as ApiResult<StartResubmittalResult>,
  withdrawResubmittal: async (id: string, reason: string): Promise<ApiResult<unknown>> =>
    (await api.post(`/umed/reversals/${id}/withdraw`, { reason })) as ApiResult<unknown>,
  addReversalReason: async (reversalId: string, reason: string): Promise<ApiResult<{ reasonId: string }>> =>
    (await api.post(`/umed/reversals/${reversalId}/reasons`, { reason })) as ApiResult<{ reasonId: string }>,
  reversalSubmitted: async (reversalId: string): Promise<ApiResult<{ state: ReversalState; submission: number }>> =>
    (await api.post(`/umed/reversals/${reversalId}/submitted`, {})) as ApiResult<{ state: ReversalState; submission: number }>,
  // PR #572 review: DCF's answer says whether a Finance handoff opened, and when ruling B blocked a second one, why.
  reversalOutcome: async (reversalId: string, outcome: ReversalOutcome): Promise<ApiResult<ReversalOutcomeResult>> =>
    (await api.post(`/umed/reversals/${reversalId}/outcome`, { outcome })) as ApiResult<ReversalOutcomeResult>,
  /** The packet went to DCF (packet §24). Recorded as eMerge's own fact, citing the packet version; the case leaves Needs creation by derivation. */
  claimCase: async (id: string): Promise<ApiResult<{ operatorUserId: string; claimed: boolean; obligationsTaken?: number }>> =>
    (await api.post(`/umed/cases/${id}/claim`, {})) as ApiResult<{ operatorUserId: string; claimed: boolean; obligationsTaken?: number }>,
  recordSubmission: async (id: string, body: { method: SubmissionMethod; reference?: string; packet_id: string; idempotency_key?: string }): Promise<ApiResult<{ submissionId: string; purpose: CreationReason; reviewDueOn?: string | null; replayed?: boolean; approved?: boolean }>> =>
    (await api.post(`/umed/cases/${id}/submissions`, body)) as ApiResult<{ submissionId: string; purpose: CreationReason; reviewDueOn?: string | null; replayed?: boolean; approved?: boolean }>,
  /* Workforce write cutover: the native packet, and the documents a case depends on. */
  packets: async (id: string): Promise<ApiResult<{ items: Packet[]; template_version: string }>> => (await api.get(`/umed/cases/${id}/packets`)) as ApiResult<{ items: Packet[]; template_version: string }>,
  previewPacket: async (id: string, inputs: PacketInputs): Promise<ApiResult<PacketPreview>> => (await api.post(`/umed/cases/${id}/packets/preview`, { inputs })) as ApiResult<PacketPreview>,
  generatePacket: async (id: string, inputs: PacketInputs, idempotencyKey: string): Promise<ApiResult<{ packetId: string; version: number; documentId: string; validation: PacketValidation; replayed: boolean }>> =>
    (await api.post(`/umed/cases/${id}/packets`, { inputs, idempotency_key: idempotencyKey })) as ApiResult<{ packetId: string; version: number; documentId: string; validation: PacketValidation; replayed: boolean }>,
  /* P0 2026-10-07: the automatically prepared packet. Nothing here sends anything to DCF. */
  packetPreparation: async (id: string): Promise<ApiResult<PacketPreparationStatus>> => (await api.get(`/umed/cases/${id}/packet-preparation`)) as ApiResult<PacketPreparationStatus>,
  preparePacket: async (id: string): Promise<ApiResult<PreparationOutcome>> => (await api.post(`/umed/cases/${id}/packet-preparation`, {})) as ApiResult<PreparationOutcome>,
  /** Ruling R2: "I verified this corrected packet is accurate: the right resident's documents only." For this exact revision. */
  confirmPacketAccuracy: async (id: string, body: { packet_id: string; sha256: string; confirmed: true }): Promise<ApiResult<{ confirmationId: string; packetId: string; packetVersion: number; confirmedAt: string; replayed: boolean }>> =>
    (await api.post(`/umed/cases/${id}/packet-preparation/accuracy-confirmation`, body)) as ApiResult<{ confirmationId: string; packetId: string; packetVersion: number; confirmedAt: string; replayed: boolean }>,
  reviewPacket: async (id: string, body: { packet_id: string; sha256: string; complete: true; warnings_acknowledged: true }):
    Promise<ApiResult<{ packetId: string; sha256: string; reviewedAt: string; stageAdvanced: boolean; accountReady: boolean }>> =>
    (await api.post(`/umed/cases/${id}/packet-preparation/review`, body)) as ApiResult<{ packetId: string; sha256: string; reviewedAt: string; stageAdvanced: boolean; accountReady: boolean }>,
  regenerateCorrectionPacket: async (id: string, body: { packet_id: string; sha256: string }): Promise<ApiResult<PreparationOutcome>> =>
    (await api.post(`/umed/cases/${id}/packet-preparation/regenerate-correction`, body)) as ApiResult<PreparationOutcome>,
  /** Dates the cover for upload (a new revision when the day or the downloader changed; the Sender is always the ruled one) and returns one audited URL to it. */
  downloadPacket: async (id: string): Promise<ApiResult<PacketDownload>> => (await api.post(`/umed/cases/${id}/packet-preparation/download`, {})) as ApiResult<PacketDownload>,
  recordPacketIdentifiers: async (id: string, body: { dcf_case_number?: string; medicaid_pin?: string }): Promise<ApiResult<{ recorded: string[]; unchanged: string[]; preparation: PreparationOutcome | null }>> =>
    (await api.post(`/umed/cases/${id}/packet-preparation/identifiers`, body)) as ApiResult<{ recorded: string[]; unchanged: string[]; preparation: PreparationOutcome | null }>,
  /** Attests a reversal's previously submitted terms when eMerge holds no evidence of them (clears REVERSAL_TERMS_UNKNOWN); prepares the packet again. */
  recordReversalTerms: async (id: string, body: { terms: string; total: string; submitted_on: string; reason: string }):
    Promise<ApiResult<{ reversalId: string; eventId: string; source: string; terms: string; total: number; submittedOn: string; preparation: PreparationOutcome | null }>> =>
    (await api.post(`/umed/cases/${id}/packet-preparation/reversal-terms`, body)) as ApiResult<{ reversalId: string; eventId: string; source: string; terms: string; total: number; submittedOn: string; preparation: PreparationOutcome | null }>,
  /** How a comment would print in the cover's Comments box (the renderer's own wrap); writes nothing. */
  previewCoverComment: async (id: string, comment: string): Promise<ApiResult<PacketCommentPreview>> =>
    (await api.post(`/umed/cases/${id}/packet-preparation/cover-comment/preview`, { comment })) as ApiResult<PacketCommentPreview>,
  /** Saves the cover's Comments for the current episode ('' clears them) and prepares a new revision that prints them. */
  recordCoverComment: async (id: string, body: { comment: string; based_on: string | null }): Promise<ApiResult<PacketCommentSaved>> =>
    (await api.post(`/umed/cases/${id}/packet-preparation/cover-comment`, body)) as ApiResult<PacketCommentSaved>,
  /** The operator's attestation that she uploaded this exact revision to DCF ACCESS: it is the sending. */
  confirmPacketUpload: async (id: string, body: { packet_id: string; sha256: string; attested: true; uploaded_on: string; date_attested?: boolean; reference?: string; idempotency_key: string }):
    Promise<ApiResult<PacketSendingRecorded>> =>
    (await api.post(`/umed/cases/${id}/packet-preparation/confirm-upload`, body)) as ApiResult<PacketSendingRecorded>,
  /** Record fax sending: the operator's attestation that she faxed this exact revision to DCF (outside eMerge). It is the sending, method FAX. */
  recordPacketFax: async (id: string, body: { packet_id: string; sha256: string; attested: true; faxed_on: string; date_attested?: boolean; reference?: string; idempotency_key: string }):
    Promise<ApiResult<PacketSendingRecorded>> =>
    (await api.post(`/umed/cases/${id}/packet-preparation/record-fax`, body)) as ApiResult<PacketSendingRecorded>,
  /** Fax to DCF: eMerge sends this exact reviewed revision through the fax service. A 201 may carry FAILED or UNCERTAIN: reload and read the status. */
  faxPacketToDcf: async (id: string, body: { packet_id: string; sha256: string; confirmed: true; date_attested?: boolean; idempotency_key: string }):
    Promise<ApiResult<{ transmissionId: string; status: FaxStatus; label: string; replayed: boolean; errorCode?: string | null }>> =>
    (await api.post(`/umed/cases/${id}/packet-preparation/fax-to-dcf`, body)) as ApiResult<{ transmissionId: string; status: FaxStatus; label: string; replayed: boolean; errorCode?: string | null }>,
  /** A person resolves a fax flagged for attention: an explicit, attested, reasoned act. */
  resolveFaxTransmission: async (id: string, transmissionId: string, body: { resolution: FaxResolution; reason: string; attested: true; provider_fax_id?: string; dcf_confirmed_not_received?: boolean; dcf_confirmed_received?: boolean }):
    Promise<ApiResult<{ transmissionId: string; status: FaxStatus; resolution: FaxResolution | 'PROVIDER_CONFIRMED'; sending: 'RECORDED' | 'NEEDS_ATTENTION' | 'PENDING' | null }>> =>
    (await api.post(`/umed/cases/${id}/packet-preparation/fax-transmissions/${transmissionId}/resolve`, body)) as ApiResult<{ transmissionId: string; status: FaxStatus; resolution: FaxResolution | 'PROVIDER_CONFIRMED'; sending: 'RECORDED' | 'NEEDS_ATTENTION' | 'PENDING' | null }>,
  /** PR #416: record a delivered fax again whose recording kept failing, from the fax service's own evidence. */
  retryFaxRecording: async (id: string, transmissionId: string): Promise<ApiResult<{ transmissionId: string; status: FaxStatus; sending: 'RECORDED' | 'NEEDS_ATTENTION' | 'PENDING' | null; code: string | null }>> =>
    (await api.post(`/umed/cases/${id}/packet-preparation/fax-transmissions/${transmissionId}/retry-recording`, {})) as ApiResult<{ transmissionId: string; status: FaxStatus; sending: 'RECORDED' | 'NEEDS_ATTENTION' | 'PENDING' | null; code: string | null }>,
  /** One short-lived URL for one document. Audited server-side; opened, never stored. */
  documentUrl: async (caseId: string, documentId: string): Promise<ApiResult<{ url: string; expires_in: number }>> =>
    (await api.post(`/umed/cases/${caseId}/documents/${documentId}/url`, {})) as ApiResult<{ url: string; expires_in: number }>,
  /* Cutover 2026-09-21: a UMED operator adds a file the case depends on, and keeps notes on the case. */
  uploadDocument: async (caseId: string, documentClass: UmedUploadClass, file: File, idempotencyKey: string, overrideReason?: string): Promise<ApiResult<{ documentId: string; documentClass: string; version: number; deduplicated: boolean }>> => {
    const form = new FormData();
    form.append('document_class', documentClass);
    form.append('idempotency_key', idempotencyKey);
    if (overrideReason) form.append('override_reason', overrideReason);
    form.append('file', file);
    return (await apiUpload(`/umed/cases/${caseId}/documents`, form)) as unknown as ApiResult<{ documentId: string; documentClass: string; version: number; deduplicated: boolean }>;
  },
  /** "Not this resident's" (2026-10-14): the row is kept and marked; optionally filed on the right resident. */
  markNotThisResidents: async (caseId: string, docId: string, body: { reason: string; correct_patient_id: string | null; idempotency_key: string }): Promise<ApiResult<NotThisResidentsMarked>> =>
    (await api.post(`/umed/cases/${caseId}/documents/${docId}/not-this-residents`, body)) as ApiResult<NotThisResidentsMarked>,
  /** Residents the caller could file this document on (their facility scope), by name. */
  residentCandidates: async (caseId: string, q: string): Promise<ApiResult<{ items: Array<{ id: string; name: string; facility_name?: string | null }> }>> =>
    (await api.get(`/umed/cases/${caseId}/resident-candidates${qs({ q })}`)) as ApiResult<{ items: Array<{ id: string; name: string; facility_name?: string | null }> }>,
  /** Open a corrected re-send after a wrong document went to DCF: not a DCF resubmission. */
  openCorrectedResend: async (caseId: string, body: { reason: string; cover_comment?: string; idempotency_key: string }): Promise<ApiResult<{ caseId: string; eventId: string; episodeKey: string }>> =>
    (await api.post(`/umed/cases/${caseId}/corrected-resend`, body)) as ApiResult<{ caseId: string; eventId: string; episodeKey: string }>,
  /** Ruling 2026-10-15: withdraw a corrected re-send opened by mistake (only while it is open; the server re-checks everything). */
  withdrawCorrectedResend: async (caseId: string, body: { reason: string; event_id: string; idempotency_key: string }): Promise<ApiResult<{ caseId: string; eventId: string; state: 'WITHDRAWN'; replayed: boolean }>> =>
    (await api.post(`/umed/cases/${caseId}/corrected-resend/withdraw`, body)) as ApiResult<{ caseId: string; eventId: string; state: 'WITHDRAWN'; replayed: boolean }>,
  addNote: async (caseId: string, note: string, idempotencyKey: string): Promise<ApiResult<{ noteId: string; recordedAt: string; replayed: boolean }>> =>
    (await api.post(`/umed/cases/${caseId}/notes`, { note, idempotency_key: idempotencyKey })) as ApiResult<{ noteId: string; recordedAt: string; replayed: boolean }>,

  /* ── Processes 5 and 6 (2026-10-12): after approval ── */
  /** PL correction: the new PL in integer cents, the month it takes effect ('YYYY-MM') and why. Opens an SFS correction episode. */
  recordPlCorrection: async (id: string, body: { new_pl_cents: number; effective_month: string; reason: string; idempotency_key: string }):
    Promise<ApiResult<PlCorrectionRecorded>> => (await api.post(`/umed/cases/${id}/pl-correction`, body)) as ApiResult<PlCorrectionRecorded>,
  /** The PL correction's outcome: CORRECTED (stays approved) or DNQ (NO LONGER QUALIFIES; effective_on defaults to today). */
  resolvePlCorrection: async (plCorrectionId: string, body: { outcome: 'CORRECTED' | 'DNQ'; note: string; effective_on?: string; idempotency_key: string }):
    Promise<ApiResult<PlCorrectionResolved>> => (await api.post(`/umed/pl-corrections/${plCorrectionId}/resolve`, body)) as ApiResult<PlCorrectionResolved>,
  /** Medicaid benefits lost: effective date (defaults to today server-side) and why. Suspends UMED work and opens one re-vet. */
  recordBenefitsLost: async (id: string, body: { effective_on?: string; reason: string; idempotency_key: string }):
    Promise<ApiResult<BenefitsLostRecorded>> => (await api.post(`/umed/cases/${id}/benefits-lost`, body)) as ApiResult<BenefitsLostRecorded>,
};

export interface NotThisResidentsMarked {
  correctionId: string; documentId: string; outcome: 'MISFILED_ONLY' | 'REFILED' | 'ALREADY_ON_RESIDENT'; refiledDocumentId: string | null;
  correctPatientId: string | null; sentIn: unknown; message: string | null; replayed: boolean;
}
export interface PlCorrectionRecorded { plCorrectionId: string; correctionId: string; caseId: string; state: 'OPEN'; newPlCents: number; effectiveMonth: string; statusWord: string | null; financeFlagged: boolean; replayed: boolean }
export interface PlCorrectionResolved { plCorrectionId: string; caseId: string; outcome: 'CORRECTED' | 'DNQ'; state: string; statusWord: string | null; financeHandoffAmended: boolean; amendmentId: string | null; financeFlagged: boolean; cancelledActions: number; replayed: boolean }
export interface BenefitsLostRecorded { benefitsLostId: string; caseId: string; effectiveOn: string; revetDueOn: string; revetDays: number; revetOwnerUserId: string | null; revetOwnerName: string | null; revetActionId: string | null; statusWord: string | null; financeFlagged: boolean; replayed: boolean }

/** A person's display name, without inventing one when both parts are absent. */
export function personName(p: { first_name?: string | null; last_name?: string | null }): string {
  const name = [p.first_name, p.last_name].filter(Boolean).join(' ').trim();
  return name || 'Name not recorded';
}

/** Calendar days read as the UTC date — the same rule as the provider portal (#97). */
function calendarDay(value: string | null | undefined): { y: number; m: number; d: number } | null {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return { y: parsed.getUTCFullYear(), m: parsed.getUTCMonth(), d: parsed.getUTCDate() };
}
/**
 * A DATE as 'YYYY-MM-DD', for a date input (2026-10-15, verifier F-B): its calendar day by the UTC reading shortDate
 * uses -- a plain '2026-10-10', or the driver's local-midnight instant serialized as '2026-10-10T00:00:00.000Z' on a UTC
 * host -- never the browser's local getters, which put a Florida browser on the 9th.
 */
export function isoDay(value: string | null | undefined): string {
  if (value && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const c = calendarDay(value);
  return c ? `${c.y}-${String(c.m + 1).padStart(2, '0')}-${String(c.d).padStart(2, '0')}` : '';
}
export function shortDate(value: string | null | undefined): string {
  const c = calendarDay(value);
  if (!c) return '—';
  return new Date(Date.UTC(c.y, c.m, c.d)).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' });
}
/**
 * A date the way the BOARD writes it: `9/1/26`.
 *
 * Josie Evans, 2026-09-29, comparing a native terms cell against a board one:
 * "The 'August 1-May 1 2027 settled here' doesn't look right." She was reading
 * eMerge's own terms beside `source_terms_raw`, which every one of the 1,505
 * board rows renders as `9/1/26-2/1/27 (6 months at $966.16 = $5,797)`. The
 * fact was the same and the shape was not, so the column looked like two.
 *
 * UTC-anchored like `shortDate`, so a term starting on the 1st never reads as
 * the previous month's last day for a viewer west of UTC.
 */
export function boardDate(value: string | null | undefined): string {
  const c = calendarDay(value);
  if (!c) return '—';
  return `${c.m + 1}/${c.d}/${String(c.y).slice(2)}`;
}
/** An instant (a sync, a projection), in the viewer's own time — not a board date. */
export function shortDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}
export function daysFromToday(value: string | null | undefined): number | null {
  const c = calendarDay(value);
  if (!c) return null;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  return Math.round((new Date(c.y, c.m, c.d).getTime() - today) / 86400000);
}
export function relativeDay(value: string | null | undefined): string | null {
  const days = daysFromToday(value);
  if (days === null) return null;
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days === -1) return 'yesterday';
  return days > 0 ? `in ${days} days` : `${Math.abs(days)} days ago`;
}

/**
 * What this case is, in one line an operator recognises. The exception wins
 * over the stage when one is open, because a reversal is what the case IS,
 * not a colour on a submitted UMED.
 */
export function stateLine(
  c: Pick<UmedCase, 'stage' | 'exception' | 'exception_state' | 'exception_open' | 'lifecycle'> & { effective_state?: EffectiveState; correction?: OpenCorrection | null; approval?: { canonical?: string } | null },
): string {
  // One sentence, the server's. A board exception older than the approval is
  // history and is shown as history (see provenanceLine), never here.
  if (c.effective_state) {
    if (c.lifecycle && c.effective_state.state !== 'CORRECTION_REQUIRED') return LIFECYCLE_LABEL[c.lifecycle];
    return c.effective_state.label;
  }
  if (exceptionShown(c) && c.exception !== 'NONE') {
    const s = c.exception_state && c.exception_state !== 'OPEN' ? ` · ${EXCEPTION_STATE_LABEL[c.exception_state]}` : '';
    return `${EXCEPTION_LABEL[c.exception]}${s}`;
  }
  if (c.lifecycle) return LIFECYCLE_LABEL[c.lifecycle];
  return STAGE_LABEL[c.stage];
}

/**
 * The second line an approved row carries: where Finance stands. "Missing" is
 * a defect with an owner, not an empty date (packet §5).
 */
export function financeLine(c: Pick<UmedCase, 'finance_handoff'>): string | null {
  const h = c.finance_handoff;
  if (!h || h.handoff === 'NOT_APPLICABLE') return null;
  return FINANCE_HANDOFF_LABEL[h.handoff] || null;
}

/**
 * What the board used to say, once the platform knows better. Shown under
 * history, so the older word is never lost and never argued with.
 */
export function provenanceLine(c: Pick<UmedCase, 'effective_state'>): string | null {
  const p = c.effective_state && c.effective_state.provenance;
  if (!p || !p.superseded_exception) return null;
  const word = p.source_status_raw ? `“${p.source_status_raw}”` : p.superseded_exception.toLowerCase();
  return `The board still says ${word}; ${p.superseded_by} since.`;
}

/**
 * The one sentence that must never be wrong. A source claim with no
 * evidence is never rendered as an approval.
 */
export function approvalLine(a: Approval): { text: string; tone: 'ok' | 'attn' | 'plain' } {
  if (a.canonical === 'PROVEN') return { text: a.basis === 'FLMMIS_ONLY' ? 'Fully approved · on FLMMIS alone' : 'Fully approved', tone: 'ok' };
  // 2026-10-06: the evidence never approves; the case waits for the operator to move it to Approved. Since 2026-10-13
  // ready_to_approve means BOTH facts the act requires are on record: NOCA received and FLMMIS reflected.
  if (a.ready_to_approve) return { text: 'NOCA received · FLMMIS reflected · not yet approved', tone: 'attn' };
  if (a.conflict) return { text: 'Source says approved · evidence incomplete', tone: 'attn' };
  return { text: 'Not yet approved', tone: 'plain' };
}

/**
 * A term's dates, typed the way she writes them.
 *
 * Josie Evans has asked three times, most plainly on 2026-09-29: "I just want
 * to be able to type it in instead of clicking on boxes." Her own format is
 * the one the board has always used — `8/1/26-11/1/26`.
 *
 * Built from STRING PARTS and never `new Date(y, m, d)`, because a Date is in
 * the browser's zone and `toISOString()` would move 2026-08-01 to 2026-07-31
 * for anyone west of UTC. eMerge has been bitten by exactly that before.
 *
 * Returns null when it cannot read the text, so the caller refuses rather than
 * recording a date nobody typed. A two-digit year is 20xx: these are Medicaid
 * terms, and there are no 1926 approvals.
 */
export function parseTermDates(text: string): { starts_on: string; ends_on: string | null } | null {
  const raw = String(text || '').trim();
  if (!raw) return null;
  /*
   * Matched whole, never split. Splitting on a hyphen ate the hyphens inside
   * `2026-08-01` and read a plain ISO date as an unreadable range, so the
   * separator is recognised only BETWEEN two complete dates.
   */
  const D = '(\\d{4}-\\d{1,2}-\\d{1,2}|\\d{1,2}[/-]\\d{1,2}[/-]\\d{2,4})';
  const asRange = new RegExp(`^${D}\\s*(?:\\bto\\b|—|–|-)\\s*${D}$`, 'i').exec(raw);
  const asOne = new RegExp(`^${D}$`).exec(raw);
  if (!asRange && !asOne) return null;
  const parts = asRange ? [asRange[1], asRange[2]] : [(asOne as RegExpExecArray)[1]];
  const one = (p: string): string | null => {
    let m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(p);
    let y: string; let mo: string; let d: string;
    if (m) { [, y, mo, d] = m; } else {
      m = /^(\d{1,2})[/-](\d{1,2})[/-](\d{2}|\d{4})$/.exec(p);
      if (!m) return null;
      const [, mm, dd, yy] = m;
      y = yy.length === 2 ? `20${yy}` : yy; mo = mm; d = dd;
    }
    const mi = Number(mo); const di = Number(d); const yi = Number(y);
    if (mi < 1 || mi > 12 || di < 1 || di > 31 || yi < 2000 || yi > 2099) return null;
    // Refuse 31 February rather than letting it roll into March.
    const last = [31, (yi % 4 === 0 && yi % 100 !== 0) || yi % 400 === 0 ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][mi - 1];
    if (di > last) return null;
    return `${y}-${String(mi).padStart(2, '0')}-${String(di).padStart(2, '0')}`;
  };
  const a = one(parts[0]);
  if (!a) return null;
  if (parts.length === 1) return { starts_on: a, ends_on: null };
  const b = one(parts[1]);
  if (!b) return null;
  if (b < a) return null;
  return { starts_on: a, ends_on: b };
}

/**
 * The submitted terms, typed as one line (Josie Evans, 2026-10-02): "Just let
 * me type in the box, I do not need different boxes to click on for
 * months/payments each month."
 *
 * The SAME reading the server does (backend services/umed/termsText.js), so
 * the screen can say what it read before she records. The server decides what
 * is stored; a line that does not read is still kept, as typed, with no
 * numbers. `9/1/26-3/1/27 (7 months at $828.14 = $5,797)`, `7 months at
 * 828.14`, `7 months at $828.14 = $5,797`.
 */
export interface TermsLine {
  months: number | null; monthly_cents: number | null; total_cents: number | null;
  starts_on: string | null; ends_on: string | null;
  /** Months AND the monthly amount were read. */
  parsed: boolean;
  /** Months were read, with no amount: the screen can still work the amount out from a closed lead total. */
  months_only: number | null;
}
export function parseTermsLine(text: string): TermsLine {
  const raw = String(text || '').trim();
  const out: TermsLine = { months: null, monthly_cents: null, total_cents: null, starts_on: null, ends_on: null, parsed: false, months_only: null };
  if (!raw) return out;
  const money = (v: string | undefined): number | null => {
    if (v === undefined) return null;
    const n = Number(v.replace(/,/g, ''));
    return Number.isFinite(n) && n > 0 ? Math.round(n * 100) : null;
  };
  const D = '(\\d{4}-\\d{1,2}-\\d{1,2}|\\d{1,2}/\\d{1,2}/\\d{2,4})';
  const range = new RegExp(`${D}\\s*(?:to|-|\u2013|\u2014)\\s*${D}`, 'i').exec(raw);
  const dates = range ? parseTermDates(`${range[1]} - ${range[2]}`) : (() => {
    const one = new RegExp(`^${D}\\b`).exec(raw);
    return one ? parseTermDates(one[1]) : null;
  })();
  if (dates) { out.starts_on = dates.starts_on; out.ends_on = dates.ends_on; }
  const mm = /(\d{1,3})\s*(?:months?|mos?\.?)(?:\s*(?:at|@|x|of)\s*\$?\s*([\d,]+(?:\.\d{1,2})?))?/i.exec(raw);
  if (!mm) return out;
  const n = Number(mm[1]);
  if (!Number.isInteger(n) || n < 1 || n > 60) return out;
  const monthly = money(mm[2]);
  if (monthly === null) { out.months_only = n; return out; }
  out.months = n;
  out.monthly_cents = monthly;
  out.parsed = true;
  /*
   * A total counts only when it is written after "=" FOLLOWING the months
   * clause; with none, it is months x monthly (verification of 1d86ec3:
   * "$5,797 = 7 months at 828.14" had read a total of $7.00).
   */
  const total = /^\s*=\s*\$?\s*([\d,]+(?:\.\d{1,2})?)/.exec(raw.slice(mm.index + mm[0].length));
  out.total_cents = (total ? money(total[1]) : null) ?? n * monthly;
  return out;
}

/* ── DCF mail (UM-R5, 2026-10-02) ─────────────────────────────────────────
 *
 * What DCF sent to the generated DCF logins, read in by the ingest worker.
 * The list names a resident by CounselEAR id and initials; the subject, the
 * excerpt and the attachments come with one message. Opening the original or
 * an attachment is audited on the server before a link exists.
 */
export type DcfMailClass = 'NOCA' | 'PASSWORD' | 'VERIFICATION' | 'NOTICE_POSTED' | 'OTHER' | 'UNREADABLE';
export type DcfMailMatch = 'MATCHED' | 'UNMATCHED' | 'AMBIGUOUS' | 'NOT_LOGIN_DOMAIN' | 'NOT_READ';
export type DcfMailAttach = 'ATTACHED' | 'NO_USABLE_ATTACHMENT' | 'HELD_NO_TRUSTED_SENDERS' | 'HELD_UNAUTHENTICATED' | 'HELD_UNLISTED_SENDER' | 'HELD_UNSCANNED' | 'HELD_VIRUS' | 'HELD_RECIPIENT_MISMATCH' | 'NO_RESIDENT' | 'NOT_APPLICABLE';
export type DcfMailState = 'open' | 'handled' | 'all';
export const DCF_MAIL_CLASS_LABEL: Record<DcfMailClass, string> = {
  NOCA: 'NOCA', PASSWORD: 'Password', VERIFICATION: 'Account verification', NOTICE_POSTED: 'Notice posted at DCF', OTHER: 'Other', UNREADABLE: 'Could not be read',
};
export const DCF_MAIL_CLASSES: DcfMailClass[] = ['NOCA', 'NOTICE_POSTED', 'VERIFICATION', 'PASSWORD', 'OTHER', 'UNREADABLE'];
export const DCF_MAIL_MATCH_LABEL: Record<DcfMailMatch, string> = {
  MATCHED: 'Matched', UNMATCHED: 'No DCF login on record has this address', AMBIGUOUS: 'Addressed to more than one resident',
  NOT_LOGIN_DOMAIN: 'Not addressed to a DCF login', NOT_READ: 'Not read',
};
export const DCF_MAIL_ATTACH_LABEL: Record<DcfMailAttach, string> = {
  ATTACHED: 'Filed on the case as a NOCA, not yet reviewed', NO_USABLE_ATTACHMENT: 'No PDF or image came with it: the notice is likely in the DCF account',
  HELD_NO_TRUSTED_SENDERS: 'Held: no trusted DCF sender is configured yet, so nothing is filed by mail',
  HELD_UNAUTHENTICATED: 'Held: the sender could not be confirmed', HELD_UNLISTED_SENDER: 'Held: the sender is not on the trusted list',
  HELD_UNSCANNED: 'Held: no virus scan result',
  HELD_RECIPIENT_MISMATCH: 'Held: the message names a different DCF login than the one it reached',
  HELD_VIRUS: 'Held: the virus scan failed', NO_RESIDENT: 'The case has no resident linked', NOT_APPLICABLE: '',
};
export interface DcfMailItem {
  id: string; received_at: string; classification: DcfMailClass; classification_rule: string | null; match_outcome: DcfMailMatch;
  to_address: string | null; from_domain: string | null; case_id: string | null; case_stage: Stage | null;
  counselear_id: string | null; initials: string | null; facility_name: string | null;
  attachments_count: number; documents_attached: number; attach_outcome: DcfMailAttach; sender_trust: string | null; error: string | null;
  handled: { at: string; by_name: string | null; reason: string | null } | null;
  /** 2026-10-22: whether THIS message's originals open for this caller (its case's building in their document scope). */
  can_open?: boolean;
}
export interface DcfMailAttachment { index: number; filename: string | null; content_type: string | null; size: number; accepted: boolean; reason: string | null; document_id: string | null; deduplicated: boolean; openable: boolean }
export interface DcfMailDetail extends DcfMailItem {
  subject: string | null; text_excerpt: string | null; from_address: string | null; sent_at: string | null;
  verdicts: Record<string, string>; attachments: DcfMailAttachment[]; raw_openable: boolean;
}
export interface DcfMailPage {
  items: DcfMailItem[]; total: number; page: number; limit: number; open_by_class: Partial<Record<DcfMailClass, number>>;
  ingest: 'ON' | 'OFF'; can: { mark_handled: boolean; open: boolean };
}
export const dcfMailApi = {
  list: async (l: { state?: DcfMailState; cls?: DcfMailClass | null; caseId?: string | null; q?: string; page?: number }): Promise<ApiResult<DcfMailPage>> =>
    (await api.get(`/umed/dcf-mail${qs({ state: l.state, class: l.cls, case_id: l.caseId, q: l.q, page: l.page })}`)) as ApiResult<DcfMailPage>,
  detail: async (id: string): Promise<ApiResult<DcfMailDetail>> => (await api.get(`/umed/dcf-mail/${id}`)) as ApiResult<DcfMailDetail>,
  rawUrl: async (id: string): Promise<ApiResult<{ url: string; expires_in: number }>> =>
    (await api.post(`/umed/dcf-mail/${id}/raw-url`, {})) as ApiResult<{ url: string; expires_in: number }>,
  attachmentUrl: async (id: string, index: number): Promise<ApiResult<{ url: string; expires_in: number }>> =>
    (await api.post(`/umed/dcf-mail/${id}/attachments/${index}/url`, {})) as ApiResult<{ url: string; expires_in: number }>,
  markHandled: async (id: string, reason: string | null): Promise<ApiResult<{ id: string; handled_at: string; replayed: boolean }>> =>
    (await api.post(`/umed/dcf-mail/${id}/handled`, { reason })) as ApiResult<{ id: string; handled_at: string; replayed: boolean }>,
};

/* ── The approval email (Kaleigh Vazquez & Josie Evans, 2026-10-08; Isaiah Cotto, 2026-10-13) ───────────────────
 * Mirrors backend services/umed/approvalEmail.js. Josie verifies, edits and sends; eMerge never sends on its own. */
export type FundsStatus = 'FACILITY' | 'FAMILY' | 'ACTION_REQUIRED';
export type ApprovalEmailState = 'SENDING' | 'SENT' | 'FAILED' | 'UNKNOWN' | 'PREPARING' | 'PREPARED_FOR_OUTLOOK';
export interface ApprovalEmailDocument {
  id: string; document_class: 'NOCA' | 'FLMMIS' | 'ACH'; sha256: string; version: number | null; byte_size: number | null;
  content_type: string | null; original_filename: string | null; created_at: string; on_this_case: boolean;
  /** A case-less document uploaded before the episode began: selectable, never pre-selected, and carries `warning`. */
  before_episode?: boolean; warning?: string | null;
}
export interface ApprovalEmailAttachment { document_class: 'NOCA' | 'FLMMIS' | 'ACH'; label: string; present: boolean; selected: ApprovalEmailDocument | null; candidates: ApprovalEmailDocument[] }
export interface ApprovalEmailRecipient {
  key: string; role: 'FACILITY_BOM' | 'FACILITY_ABOM' | 'FACILITY_SSD' | 'FACILITY_ADMINISTRATOR' | 'POA' | 'RESPONSIBLE_PARTY';
  label: string; name: string | null; title: string | null; email: string | null; email_present: boolean; source: string | null; pre_selected: boolean;
}
export interface ApprovalEmailNotice { code: string; message: string; document_class?: string }
export interface ApprovalEmailHistoryItem {
  id: string; mode: 'SEND' | 'OUTLOOK'; state: ApprovalEmailState; requested_at: string; completed_at: string | null;
  to: Array<{ address: string; role: string; name: string | null }>; cc: Array<{ address: string; role: string; name: string | null }>;
  outcome_code: string | null; actor_name: string | null;
  /** PR #416: attachments marked "Not this resident's" while this email was being sent. */
  marked_during_send?: Array<{ document_class: string }>;
}
export interface ApprovalEmailChecklist {
  case_id: string;
  approval: { approved: boolean; kind: 'APPROVED' | 'APPROVED_SFS_CORRECTION'; approved_on: string | null };
  resident: { name: string | null };
  facility: { id: string | null; name: string | null };
  terms: { source: 'SUBMITTED_PACKET' | 'CASE_TERMS' | null; label: string; text: string | null; total_cents: number | null; submitted_on: string | null; packet_version: number | null; current_text: string | null; differs_from_current: boolean };
  funds_manager: { type: string | null; label: string; status: FundsStatus; reason: 'UNKNOWN' | 'NOT_COVERED' | null; source: string; ach_on_file: boolean; board_disagrees: boolean };
  poa: { name: string | null; email: string | null; phone: string | null };
  responsible_party: { name: string | null; email: string | null; phone: string | null; source: string | null };
  facility_contacts: Record<'BOM' | 'ABOM' | 'SSD' | 'ADMINISTRATOR', Array<{ id: string; name: string | null; title: string | null; email: string | null; source: string }>>;
  required_attachments: ApprovalEmailAttachment[];
  recipients: ApprovalEmailRecipient[];
  gaps: ApprovalEmailNotice[];
  warnings: ApprovalEmailNotice[];
  sender: { name: string | null; email: string | null };
  send: { env: string; enabled: boolean; on: boolean; transport: string | null; gaps: ApprovalEmailNotice[]; estimated_bytes: number; max_bytes: number };
  outlook: { enabled: boolean };
  history: ApprovalEmailHistoryItem[];
  migration_ready: boolean;
  fingerprint: string;
  writes_open?: boolean;
}
export interface ApprovalEmailDraft { subject: string; body: string; body_version: string; source: 'DEFAULT' | 'ACTIVE_VERSION'; missing: string[]; fingerprint: string }
export interface ApprovalEmailSendBody {
  recipients: string[]; cc?: string[]; subject: string; body: string; attachment_ids: string[];
  checklist_confirmed: { terms: boolean; funds_manager: boolean; poa: boolean; facility_contacts: boolean };
  idempotency_key: string; confirm_resend?: boolean; checklist_fingerprint?: string; body_version?: string;
}
export interface ApprovalEmailResult {
  emailId: string; mode: 'SEND' | 'OUTLOOK'; state: ApprovalEmailState; outcomeCode: string | null; replayed: boolean;
  requestedAt: string; completedAt: string | null; to: ApprovalEmailHistoryItem['to']; cc: ApprovalEmailHistoryItem['cc'];
  filename?: string; emlBase64?: string;
  /** PR #416: attachments marked "Not this resident's" while the email was being sent (it was sent as listed). */
  markedDuringSend?: Array<{ document_id: string; document_class: 'NOCA' | 'FLMMIS' | 'ACH' }>;
}
export const approvalEmailApi = {
  checklist: async (id: string): Promise<ApiResult<ApprovalEmailChecklist>> =>
    (await api.get(`/umed/cases/${id}/approval-email/checklist`)) as ApiResult<ApprovalEmailChecklist>,
  draft: async (id: string): Promise<ApiResult<ApprovalEmailDraft>> =>
    (await api.get(`/umed/cases/${id}/approval-email/draft`)) as ApiResult<ApprovalEmailDraft>,
  /** eMerge sends it (only when the server says sending is on). One key per deliberate click: a retry never sends twice. */
  send: async (id: string, body: ApprovalEmailSendBody): Promise<ApiResult<ApprovalEmailResult>> =>
    (await api.post(`/umed/cases/${id}/approval-email/send`, body)) as ApiResult<ApprovalEmailResult>,
  /** A .eml with the attachments, opened in Outlook as a draft she sends herself. */
  eml: async (id: string, body: ApprovalEmailSendBody): Promise<ApiResult<ApprovalEmailResult>> =>
    (await api.post(`/umed/cases/${id}/approval-email/eml`, body)) as ApiResult<ApprovalEmailResult>,
};
